import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import {
  Spade,
  Club,
  Sparkles,
  Send,
  ChevronDown,
  ChevronUp,
  UserPlus,
  X,
  Star,
  Mail,
  Coins,
  CircleAlert,
  BadgeCheck,
  Clock,
  Globe2,
  Users,
  Shield,
} from 'lucide-react';
import { ColyseusLobbyTransport } from './realtime/colyseusLobbyTransport';
import { hubPhaseToHeaderConnectionStatus, HUB_SOCKET_PHASE } from './realtime/connectionUx';
import AuthPage from './components/AuthPage';
import Lobby from './components/Lobby';
import GameTable from './components/GameTable';
import BaseGameRoomScreen from './components/online/BaseGameRoomScreen';
import CryptoWallet from './components/CryptoWallet';
import LocalGame from './components/LocalGame';
import MobileLocalGame from './components/MobileLocalGame';
import OnlineLobby from './components/OnlineLobby';
import PokerLobby from './components/PokerLobby';
import PokerHuPractice from './components/PokerHuPractice';
import BelotHub from './components/BelotHub';
import BelotLobby from './components/BelotLobby';
import { getPlayerLeague } from './components/lobbyHubShared';
import { LobbyHubHeader } from './components/LobbyHubHeader';
import { LOBBY_HOME_ASSETS } from './lib/lobbyHomeAssets';
import { LobbyProfileScreen } from './components/LobbyProfileScreen';
import { LobbyDailyRewardsScreen } from './components/LobbyDailyRewardsScreen';
import { LobbyFriendGiftsScreen } from './components/LobbyFriendGiftsScreen';
import { LobbyFriendsScreen } from './components/LobbyFriendsScreen';
import { LobbyShopScreen } from './components/LobbyShopScreen';
import { LoginPromoCarousel } from './components/LoginPromoCarousel';
import { MaintenanceLockScreen } from './components/MaintenanceLockScreen';
import './App.css';
import './components/Lobby.css';
import { getApiBaseUrl } from './lib/apiBaseUrl';
import { POKER_LOBBY_TEMP_DISABLED } from './lib/pokerLobbyStub';
import { useSocialLobby } from './social/useSocialLobby';
import { mergeFriendRequestsLocalAndServer, parseLobbyMessagesFromStorage, SYSTEM_CHAT_SENDER } from './social/socialLobbyModel';

const avatarLocalStorageKey = (userId) => `seka_avatarUrl:${userId}`;
const DEFAULT_GUEST_AVATAR_URL = `${process.env.PUBLIC_URL || ''}/guest-avatar.png`;
const FALLBACK_GUEST_AVATAR_URL = `${process.env.PUBLIC_URL || ''}/avatar-for-bot.png`;

function normalizeUserAvatar(user) {
  if (!user || typeof user !== 'object') return user;
  const avatarUrl = String(user.avatarUrl || '').trim() || DEFAULT_GUEST_AVATAR_URL;
  return { ...user, avatarUrl };
}

const API_BASE_URL = getApiBaseUrl();

/** Совпадает с логикой `LobbyDailyRewardsScreen.statusFor`: можно забрать текущий `nextDay`. */
function isDailyRewardClaimableFromServerState(state) {
  if (!state || !state.canClaimToday) return false;
  const nd = state.nextDay;
  if (nd == null || nd === '') return false;
  const day = Number(nd);
  if (!Number.isFinite(day) || day < 1 || day > 7) return false;
  const claimed = new Set(
    (Array.isArray(state.claimedDays) ? state.claimedDays : [])
      .map((d) => Number(d))
      .filter((n) => Number.isFinite(n))
  );
  if (claimed.has(day)) return false;
  return true;
}

/** Тело для `POST /api/shop/stripe/checkout` из карточки магазина (вкл. кастомные вкладки `shopSections`). */
function resolveShopStripeCheckout(item) {
  if (!item) return null;
  if (item.kind === 'shopCustom') {
    if (item.sectionId && item.id != null && item.price != null) {
      return { kind: 'custom', sectionId: String(item.sectionId), itemId: String(item.id) };
    }
    return null;
  }
  if (item.type === 'daily' || item.type === 'hourly' || item.type === 'video') return null;
  const id = String(item.id || '').trim();
  if (!id) return null;
  if (id.startsWith('vip-')) {
    return { kind: 'vip', packageId: id };
  }
  if (item.cash != null && item.price != null) {
    if (id === 'cash-custom') {
      return { kind: 'cash', packageId: id, customCashAmount: Number(item.cash) };
    }
    return { kind: 'cash', packageId: id };
  }
  if (item.coins != null && item.price != null) {
    return { kind: 'coins', packageId: id };
  }
  return null;
}

const SEKA_CLIENT_FINGERPRINT_LS_KEY = 'seka_client_fingerprint_v1';

function randomHexBytes(byteLength) {
  const arr = new Uint8Array(byteLength);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(arr);
  } else {
    for (let i = 0; i < byteLength; i += 1) arr[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Ключ идемпотентности для `POST /api/seka/local/wallet/delta` (8+ символов `[a-zA-Z0-9_-]`). */
function generateSekaLocalWalletIdempotencyKey() {
  return `${randomHexBytes(16)}_${randomHexBytes(8)}`;
}

function getOrCreateSekaClientFingerprint() {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return '';
  try {
    let fp = String(localStorage.getItem(SEKA_CLIENT_FINGERPRINT_LS_KEY) || '').trim();
    if (fp.length >= 16 && fp.length <= 64 && /^[a-fA-F0-9]+$/.test(fp)) return fp.slice(0, 64);
    fp = randomHexBytes(16);
    localStorage.setItem(SEKA_CLIENT_FINGERPRINT_LS_KEY, fp);
    return fp;
  } catch {
    return randomHexBytes(16);
  }
}

/** В dev (`npm start`) сразу в лобби с тестовым пользователем, без экрана входа. В production-сборке отключено. */
const SKIP_LOGIN_IN_DEV = process.env.NODE_ENV === 'development';

const DEV_USER_BASE = {
  username: 'Разработчик',
  email: 'dev@local.test',
  balance: 10000,
  /** Реальные деньги (USD) для шапки лобби; с бэка — `bucksBalance` / `realBalance`. */
  bucksBalance: 10,
  level: 1,
  xpCurrent: 498,
  xpMax: 505,
  avatarUrl: DEFAULT_GUEST_AVATAR_URL,
};

const DEV_DEVICE_ID_KEY = 'seka_dev_device_id';

/** Уникальный userId на браузер в dev без входа — иначе все «гости» с dev-local рвут друг друга в Colyseus. */
function getOrCreateDevDeviceId() {
  if (typeof window === 'undefined') return 'dev_ssr';
  try {
    let id = localStorage.getItem(DEV_DEVICE_ID_KEY);
    if (!id || !/^dev_[a-zA-Z0-9_-]+$/.test(id)) {
      const rnd =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID().replace(/-/g, '')
          : `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 14)}`;
      id = `dev_${rnd}`;
      localStorage.setItem(DEV_DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return `dev_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }
}

function createDevUser() {
  return {
    ...DEV_USER_BASE,
    id: getOrCreateDevDeviceId(),
  };
}

/** Начальный `user`: в dev без JWT — createDevUser(); если в `localStorage` есть токен — `null` до `GET /api/auth/me` (иначе после F5 снова 10000/10). */
function getInitialUserState() {
  if (typeof window === 'undefined') return SKIP_LOGIN_IN_DEV ? createDevUser() : null;
  if (localStorage.getItem('token')) return null;
  return SKIP_LOGIN_IN_DEV ? createDevUser() : null;
}
const NEW_ACCOUNT_FREE_COINS = 10000;
/** Версия карусели «новости при входе»; при увеличении на сервере модалка покажется снова. */
const LOGIN_CAROUSEL_ACK_KEY = 'seka_carousel_ack_version';

const GLOBAL_LOBBY_CLAN_CHAT_KEY = 'seka-global-lobby-clan-chat-v1';
const GLOBAL_LOBBY_CHAT_MODERATION_KEY = 'seka-global-lobby-chat-moderation-v1';
const GLOBAL_LOBBY_INBOX_KEY = 'seka-global-lobby-inbox-v1';
/** Интервал опроса `/api/mail/inbox` для аккаунта с JWT (рассылка пишется только на сервер). */
const MAIL_INBOX_POLL_MS = 45_000;

const DEFAULT_LOBBY_INBOX = [
  {
    id: 'inbox-seed-system',
    kind: 'system',
    title: 'Обновление',
    body: 'В лобби добавлены почта и раздел «Закорефаниться». Следите за рассылкой — здесь будут новости и уведомления.',
    createdAt: Date.now() - 3600_000,
    read: false,
  },
  {
    id: 'inbox-seed-broadcast',
    kind: 'broadcast',
    title: 'Прогон с централа',
    body: 'Спасибо, что в братве! Скоро — турниры и кланы.',
    createdAt: Date.now() - 7200_000,
    read: true,
  },
  {
    id: 'inbox-seed-user',
    kind: 'user',
    title: 'Сообщение от игрока',
    body: 'Привет! Загляни в глобальный чат — там обсуждают новые режимы.',
    fromUsername: 'Kiryuha',
    createdAt: Date.now() - 1800_000,
    read: false,
  },
];

function normalizeInboxRow(x) {
  if (!x || typeof x !== 'object') return null;
  const id = String(x.id || '').trim();
  if (!id) return null;
  const rawTitle = String(x.title || '').trim();
  const rawBody = String(x.body || '').trim();
  const imageUrl = typeof x.imageUrl === 'string' ? x.imageUrl.trim() : '';
  const linkUrl = typeof x.linkUrl === 'string' ? x.linkUrl.trim() : '';
  const linkLabel = typeof x.linkLabel === 'string' ? String(x.linkLabel).trim() : '';
  const fromUsername = x.fromUsername != null ? String(x.fromUsername).trim() : '';
  const rewardCoins = Math.max(0, Math.floor(Number(x.rewardCoins) || 0));
  const hasContent =
    rewardCoins > 0 ||
    Boolean(rawTitle) ||
    Boolean(rawBody) ||
    Boolean(imageUrl) ||
    Boolean(linkUrl) ||
    Boolean(linkLabel) ||
    Boolean(fromUsername);
  if (!hasContent) return null;
  return {
    id,
    kind: ['system', 'broadcast', 'notification', 'user'].includes(x.kind) ? x.kind : 'notification',
    title: rawTitle || 'Уведомление',
    body: rawBody,
    fromUsername: fromUsername || undefined,
    createdAt: Number(x.createdAt) || Date.now(),
    read: Boolean(x.read),
    imageUrl,
    linkUrl,
    linkLabel,
    rewardCoins,
    rewardClaimed: Boolean(x.rewardClaimed),
  };
}

function loadLobbyInboxFromStorage() {
  try {
    const raw = localStorage.getItem(GLOBAL_LOBBY_INBOX_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.map((x) => normalizeInboxRow(x)).filter(Boolean);
  } catch {
    return null;
  }
}

const DEFAULT_LOBBY_INBOX_NORMALIZED = DEFAULT_LOBBY_INBOX.map((x) => normalizeInboxRow(x)).filter(Boolean);

/** Для гостя — локальная почта; при наличии JWT старт с пустым списком до ответа `/api/mail/inbox`. */
function getInitialLobbyInbox() {
  if (typeof window === 'undefined') return DEFAULT_LOBBY_INBOX_NORMALIZED;
  try {
    if (localStorage.getItem('token')) return [];
  } catch {
    /* ignore */
  }
  return loadLobbyInboxFromStorage() || DEFAULT_LOBBY_INBOX_NORMALIZED;
}
const DEFAULT_CLAN_LOBBY_MESSAGES = [
  {
    id: 'clan-seed-1',
    sender: SYSTEM_CHAT_SENDER,
    text: 'Здесь будут сообщения вашего клана. Пока канал локальный в браузере; синхронизация с сервером — позже.',
    createdAt: Date.now() - 96_000,
  },
];

const CLAN_CHAT_LOCKED_ROWS = [
  {
    id: 'clan-gate-local',
    sender: SYSTEM_CHAT_SENDER,
    text: 'Клановый чат откроется, когда у профиля будет тег клана. Сообщения клана появятся на сервере вместе с релизом кланов.',
    createdAt: Date.now() - 86_400_000,
  },
];

function getInitialGlobalChatModeration() {
  if (typeof window === 'undefined') return { warningCount: 0, blockedUntil: 0, lastSentAt: 0 };
  try {
    if (localStorage.getItem('token')) return { warningCount: 0, blockedUntil: 0, lastSentAt: 0 };
    const raw = localStorage.getItem(GLOBAL_LOBBY_CHAT_MODERATION_KEY);
    if (!raw) return { warningCount: 0, blockedUntil: 0, lastSentAt: 0 };
    const parsed = JSON.parse(raw);
    return {
      warningCount: Number(parsed?.warningCount) || 0,
      blockedUntil: Number(parsed?.blockedUntil) || 0,
      lastSentAt: Number(parsed?.lastSentAt) || 0,
    };
  } catch {
    return { warningCount: 0, blockedUntil: 0, lastSentAt: 0 };
  }
}

const CHAT_COOLDOWN_MS = 60_000;
/** Кулдаун для ЛС и кланового чата (локально, до появления сервера). */
const HUB_AUX_CHAT_COOLDOWN_MS = 20_000;
/** Пауза перед повторной отправкой заявки тому же игроку, пока предыдущая в статусе «ожидание». */
const FRIEND_REQUEST_RESEND_COOLDOWN_MS = 120_000;
const CHAT_BAN_MS = 24 * 60 * 60 * 1000;
const MAX_CHAT_WARNINGS = 3;
const BAD_WORD_STEMS = [
  'хуй',
  'хуе',
  'пизд',
  'еба',
  'ёба',
  'бля',
  'мраз',
  'долбо',
  'муда',
  'гандон',
  'шлюх',
  'чмо',
  'суч',
];

function AppToast({ type = 'success', message }) {
  const [coinImgMissing, setCoinImgMissing] = useState(false);
  if (!message) return null;

  const text = String(message || '');
  const isCoinsToast = /\bмонет|фишк|coins|\+\d+/i.test(text);
  const isError = type === 'error';
  const coinsMatch = text.match(/^(.*?)(\+\s?[\d\s.,]+)(.*)$/);
  const coinImgSrc = `${process.env.PUBLIC_URL || ''}/1fishka.png`;
  const Icon = isError ? CircleAlert : isCoinsToast ? Coins : BadgeCheck;
  const shellClass = isError
    ? 'border-red-400/45 from-[#3a141a]/96 via-[#281018]/96 to-[#15080c]/96'
    : isCoinsToast
      ? 'border-emerald-300/55 from-[#10351f]/96 via-[#0f2b1b]/96 to-[#0a1a10]/96'
      : 'border-cyan-300/45 from-[#10364d]/96 via-[#0e283b]/96 to-[#0a1726]/96';
  const iconClass = isError
    ? 'border-red-300/35 bg-red-500/15 text-red-200'
    : isCoinsToast
      ? 'border-emerald-300/45 bg-emerald-500/20 text-yellow-200'
      : 'border-cyan-300/35 bg-cyan-500/15 text-cyan-100';
  const textClass = isError ? 'text-red-100' : isCoinsToast ? 'text-yellow-200' : 'text-cyan-50';
  const glowClass = isError
    ? 'shadow-[0_12px_30px_rgba(220,38,38,0.28)]'
    : isCoinsToast
      ? 'shadow-[0_12px_34px_rgba(34,197,94,0.34)]'
      : 'shadow-[0_12px_32px_rgba(34,211,238,0.26)]';

  return (
    <div className="pointer-events-none fixed left-1/2 top-[max(0.7rem,env(safe-area-inset-top,0px))] z-[130] w-auto max-w-[min(calc(100%-2.25rem),21rem)] -translate-x-1/2">
      <div
        className={`relative overflow-hidden rounded-xl border bg-gradient-to-br px-3 py-2 backdrop-blur-md ${shellClass} ${glowClass} animate-[app-toast-in_360ms_cubic-bezier(0.2,0.8,0.2,1)]`}
        role={isError ? 'alert' : 'status'}
      >
        <span className="pointer-events-none absolute inset-x-0 top-0 h-[40%] bg-gradient-to-b from-white/18 to-transparent" aria-hidden />
        <div className="relative z-[1] flex items-center gap-2.5">
          <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${iconClass}`}>
            {isCoinsToast && !coinImgMissing ? (
              <img
                src={coinImgSrc}
                alt=""
                width={20}
                height={20}
                className="h-5 w-5 object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]"
                onError={() => setCoinImgMissing(true)}
                draggable={false}
                aria-hidden
              />
            ) : (
              <Icon className="h-4.5 w-4.5" aria-hidden />
            )}
          </span>
          <p className={`min-w-0 break-words leading-snug ${isCoinsToast ? 'text-[0.98rem] font-black tracking-[0.03em] sm:text-[1.02rem]' : 'text-sm font-semibold'} ${textClass}`}>
            {isCoinsToast && coinsMatch ? (
              <>
                <span className="text-yellow-200">{coinsMatch[1]}</span>
                <span className="mx-0.5 text-emerald-300 drop-shadow-[0_0_8px_rgba(74,222,128,0.6)]">
                  {coinsMatch[2]}
                </span>
                <span className="text-yellow-200">{coinsMatch[3]}</span>
              </>
            ) : (
              text
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

const normalizeForModeration = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[^a-zа-я0-9\s]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const hasBadContent = (value) => {
  const normalized = normalizeForModeration(value);
  if (!normalized) return false;
  return BAD_WORD_STEMS.some((stem) => normalized.includes(stem));
};

const getSenderUsername = (sender) => sender?.username || 'Игрок';
const getSenderClan = (sender) => sender?.clan || 'Без клана';
const getSenderDisplayLevel = (sender, currentUser) => {
  const senderId = sender?.id != null ? String(sender.id) : '';
  const currentId = currentUser?.id != null ? String(currentUser.id) : '';
  if (senderId && currentId && senderId === currentId) {
    return Number(currentUser?.level) || 1;
  }
  return Number(sender?.level) || 1;
};

const senderFromCurrentUser = (u) => ({
  id: u?.id || 'local-player',
  username: u?.username || 'Вы',
  clan: u?.clan || 'Без клана',
  level: Number(u?.level) || 1,
  avatarUrl: u?.avatarUrl || null,
});

function formatCountdownMmSs(ms) {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

function GlobalChatMessageAvatar({ sender, size = 'md' }) {
  const isSystem = sender?.id === 'system' || sender?.clan === 'SYSTEM';
  const url = typeof sender?.avatarUrl === 'string' ? sender.avatarUrl.trim() : '';
  const isCompact = size === 'xs' || size === 'sm';
  const dim =
    size === 'xs'
      ? 'h-6 w-6 sm:h-6 sm:w-6'
      : size === 'sm'
        ? 'h-8 w-8 sm:h-9 sm:w-9'
        : 'h-10 w-10 sm:h-11 sm:w-11';
  const round = isCompact ? 'rounded-full' : 'rounded-xl';
  const textSz = size === 'xs' ? 'text-[11px] leading-none' : size === 'sm' ? 'text-xs leading-none' : 'text-sm';
  const sparkSz = size === 'xs' ? 'h-3 w-3' : size === 'sm' ? 'h-3.5 w-3.5' : 'h-5 w-5';
  const shell = `relative inline-flex shrink-0 ${dim} ${round} ${textSz} select-none items-center justify-center overflow-hidden shadow-[0_1px_0_rgba(255,255,255,0.06)] ring-1 ring-inset`;
  if (url) {
    return (
      <span className={`${shell} ring-cyan-400/35 bg-[#0b1522]`}>
        <img src={url} alt="" className="h-full w-full object-cover" draggable={false} />
      </span>
    );
  }
  if (isSystem) {
    return (
      <span
        className={`${shell} bg-gradient-to-br from-cyan-900/90 to-[#071018] ring-cyan-400/30 text-cyan-100`}
        aria-hidden
      >
        <Sparkles className={sparkSz} strokeWidth={2.2} />
      </span>
    );
  }
  const ch = String(sender?.username || 'И').charAt(0).toUpperCase();
  return (
    <span
      className={`${shell} bg-gradient-to-br from-[#1c4a7a] to-[#0c1828] font-semibold tracking-tight text-white ring-amber-400/25`}
      aria-hidden
    >
      {ch}
    </span>
  );
}

function ChatRankBadge({ level }) {
  const lvl = Number(level) || 1;
  const league = getPlayerLeague(lvl);
  return (
    <span className="relative inline-flex h-5 w-5 shrink-0 items-center justify-center" aria-label={`Уровень ${lvl} · лига ${league.label}`}>
      <Star className={`absolute inset-0 h-5 w-5 drop-shadow-md ${league.starClass}`} strokeWidth={1.15} aria-hidden />
      <span
        className="relative z-[1] font-black tabular-nums text-[8px] text-white"
        style={{ WebkitTextStroke: '1px rgb(0 0 0)', paintOrder: 'stroke fill' }}
      >
        {lvl}
      </span>
    </span>
  );
}

function App() {
  const [currentView, setCurrentView] = useState(SKIP_LOGIN_IN_DEV ? 'lobby' : 'auth');
  /** После лобби: `tableId` + Colyseus `gameRoomId` для `joinById` игровой комнаты. */
  const [gameJoin, setGameJoin] = useState(null);
  const gameJoinRef = useRef(null);
  const gameTransitionByTableRef = useRef(new Map());
  const gameStartSignatureByTableRef = useRef(new Map());
  const [user, setUser] = useState(getInitialUserState);
  /** JWT из REST (тот же порт, что Colyseus) — секрет `SEKA_JWT_SECRET` совпадает с выдачей токенов; для `client.http.authToken`. */
  const [sessionToken, setSessionToken] = useState(() =>
    typeof window !== 'undefined' ? localStorage.getItem('token') : null
  );
  const [socket, setSocket] = useState(null);
  /** Фаза глобального Colyseus-лобби (хаб): не путать с `socket?.connected` при async connect. */
  const [hubSocketPhase, setHubSocketPhase] = useState(HUB_SOCKET_PHASE.IDLE);
  const hubLobbySessionRef = useRef(0);
  /** При наличии токена показываем спиннер до ответа `/api/auth/me` (в dev тоже — иначе мигание 10000/10). */
  const [loading, setLoading] = useState(
    () => typeof window !== 'undefined' && Boolean(localStorage.getItem('token'))
  );
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [onlineLobbyOrigin, setOnlineLobbyOrigin] = useState('lobby');
  /** Вход «Играть» с хаба: открыть OnlineLobby и выполнить JOIN_ROOM как у «Войти». */
  const [onlineLobbyHubPendingJoin, setOnlineLobbyHubPendingJoin] = useState(null);
  /** С хаба «Создать»: OnlineLobby + модалка создания (без create на hub-сокете). */
  const [onlineLobbyHubOpenCreateModal, setOnlineLobbyHubOpenCreateModal] = useState(false);
  /** Куда вернуться после закрытия кошелька: лобби Секи или покер */
  const walletReturnViewRef = useRef('lobby');
  /** Куда вернуться после закрытия экрана профиля */
  const profileReturnViewRef = useRef('lobby');
  const [dailyRewardsOpen, setDailyRewardsOpen] = useState(false);
  /** Красная точка на тайле «Награды» — только если сервер говорит, что сегодня можно claim. */
  const [dailyRewardsHubClaimable, setDailyRewardsHubClaimable] = useState(false);
  const [friendGiftsOpen, setFriendGiftsOpen] = useState(false);
  const [friendsOpen, setFriendsOpen] = useState(false);
  const [incomingTableInvites, setIncomingTableInvites] = useState([]);
  const [outgoingTableInvites, setOutgoingTableInvites] = useState([]);
  const [activeGameInvite, setActiveGameInvite] = useState(null);
  const inviteExpireTimersRef = useRef(new Map());
  const pendingInviteToastRef = useRef({ at: 0, targetLabel: '' });
  const inviteSuccessByIdRef = useRef(new Set());
  const acceptedInviteAutoJoinRef = useRef(new Set());
  const [shopOpen, setShopOpen] = useState(false);
  const [shopCatalog, setShopCatalog] = useState(null);
  const [publicFlags, setPublicFlags] = useState({
    shopEnabled: true,
    shopPurchasesEnabled: false,
    stripeCheckoutConfigured: false,
    globalChatEnabled: true,
    dailyRewardsEnabled: true,
    mailEnabled: true,
    hubDmChatEnabled: false,
    hubClanChatEnabled: false,
    friendGiftsScreenEnabled: false,
    sekaLocalClientWalletSyncEnabled: true,
    sekaLocalSessionEconomyEnabled: false,
  });
  const publicFlagsRef = useRef(publicFlags);
  const [publicMaintenance, setPublicMaintenance] = useState({
    enabled: false,
    message: '',
    retryAfterMinutes: null,
    allowedTesterUserIds: [],
  });
  const [loginCarouselOpen, setLoginCarouselOpen] = useState(false);
  const [loginCarouselSlides, setLoginCarouselSlides] = useState([]);
  const [loginCarouselVersion, setLoginCarouselVersion] = useState(0);
  const devLoginCarouselOfferedRef = useRef(false);
  const loginCarouselFetchAbortRef = useRef(null);
  /** Сбрасывает гонки `checkAuth` при частых F5: не применять устаревший ответ /me к состоянию. */
  const authCheckGenRef = useRef(0);
  /** Накопленные дельты SEKA из локальной секи → POST /api/seka/local/wallet/delta */
  const localSekaWalletDeltaPendingRef = useRef(0);
  const localSekaWalletDeltaTimerRef = useRef(null);
  const localSekaLimitsSnapshotRef = useRef(null);
  const globalChatScrollRef = useRef(null);

  useEffect(() => {
    publicFlagsRef.current = publicFlags;
  }, [publicFlags]);
  useEffect(() => {
    gameJoinRef.current = gameJoin;
    if (!gameJoin?.tableId) return;
    const tableId = String(gameJoin.tableId || '').trim().toUpperCase();
    const gid = String(gameJoin.gameRoomId || '').trim();
    if (!tableId || !gid) return;
    gameTransitionByTableRef.current.set(tableId, gid);
  }, [gameJoin]);
  const [globalChatCollapsed, setGlobalChatCollapsed] = useState(true);
  const [hubChatChannel, setHubChatChannel] = useState('global');
  const [globalChatDraft, setGlobalChatDraft] = useState('');
  const [dmChatDraft, setDmChatDraft] = useState('');
  const [clanChatDraft, setClanChatDraft] = useState('');
  const [hubClanLastSentAt, setHubClanLastSentAt] = useState(0);
  const [dmSelectedPeerId, setDmSelectedPeerId] = useState(null);
  const devDeviceUser = useMemo(() => createDevUser(), []);
  const lobbyUser = user ?? (SKIP_LOGIN_IN_DEV && !sessionToken ? devDeviceUser : null);

  const {
    mailFriendRequests,
    setMailFriendRequests,
    globalLobbyMessages,
    dmLobbyMessages,
    dmUnreadByPeer,
    setDmUnreadByPeer,
    hubDmFriendsList,
    appendGlobalLobbyMessageLocal,
    handleLobbyFriendRequestDelivery,
    handleLobbyFriendRequestResolved,
    sendFriendRequestToProfileUser,
    emitFriendRequestResolvedToServer,
    removeAcceptedFriendByPeerId,
    refetchFriendRequestsFromApi,
    refetchFriendPresence,
    friendPresenceByUserId,
  } = useSocialLobby({
    apiBaseUrl: API_BASE_URL,
    lobbyUser,
    sessionToken,
    socket,
    setError,
    setSuccess,
    dmSelectedPeerId,
    hubChatChannel,
    getDevDeviceId: getOrCreateDevDeviceId,
    devUserProfile: { username: DEV_USER_BASE.username, level: DEV_USER_BASE.level },
  });

  const appendGlobalLobbyMessage = appendGlobalLobbyMessageLocal;

  const [clanLobbyMessages, setClanLobbyMessages] = useState(() =>
    typeof window === 'undefined'
      ? DEFAULT_CLAN_LOBBY_MESSAGES
      : parseLobbyMessagesFromStorage(localStorage.getItem(GLOBAL_LOBBY_CLAN_CHAT_KEY), DEFAULT_CLAN_LOBBY_MESSAGES)
  );
  const [mailOpen, setMailOpen] = useState(false);
  /** id -> true means expanded; missing key = collapsed (default). */
  const [mailExpandedById, setMailExpandedById] = useState({});
  const [giftsNavBadgeCount, setGiftsNavBadgeCount] = useState(0);
  const [chatProfileUser, setChatProfileUser] = useState(null);
  const [lobbyInbox, setLobbyInbox] = useState(() => getInitialLobbyInbox());

  const refreshLobbyInboxFromApi = useCallback(async () => {
    const token = sessionToken;
    if (!token) return false;
    try {
      const r = await fetch(`${API_BASE_URL}/api/mail/inbox`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return false;
      const data = await r.json();
      const list = (data.messages || []).map(normalizeInboxRow).filter(Boolean);
      setLobbyInbox(list);
      return true;
    } catch {
      return false;
    }
  }, [sessionToken]);

  const onSocialLobbyTransportReconnected = useCallback(() => {
    refetchFriendRequestsFromApi();
    refetchFriendPresence();
    void refreshLobbyInboxFromApi();
  }, [refetchFriendRequestsFromApi, refetchFriendPresence, refreshLobbyInboxFromApi]);

  const [globalChatModeration, setGlobalChatModeration] = useState(() => getInitialGlobalChatModeration());
  const [chatNowMs, setChatNowMs] = useState(() => Date.now());
  // Colyseus Lobby (глобальный сокет главного лобби). Не поднимаем параллельно с OnlineLobby / игровым столом —
  // там свой Colyseus-клиент к `lobby` / столу; два join под одним userId рвут сессию и вызывают шторм reconnected.
  // Пока открыт стол (`game-*`), хаб к `lobby` тоже не поднимаем — иначе при возврате в OnlineLobby новый join
  // ловит ещё живой хаб-сокет и сервер шлёт duplicate kick (ложное «другое устройство»).
  useEffect(() => {
    if (!user) {
      setSocket(null);
      setHubSocketPhase(HUB_SOCKET_PHASE.IDLE);
      return undefined;
    }
    const suspendHubLobby = currentView === 'online-lobby' || currentView.startsWith('game-');
    if (suspendHubLobby) {
      hubLobbySessionRef.current += 1;
      setSocket((prev) => {
        if (prev?.disconnect) {
          try {
            prev.disconnect();
          } catch {
            /* ignore */
          }
        }
        return null;
      });
      setHubSocketPhase(HUB_SOCKET_PHASE.IDLE);
      return undefined;
    }

    const session = ++hubLobbySessionRef.current;
    setHubSocketPhase(HUB_SOCKET_PHASE.CONNECTING);

    let transport = null;
    let detachTransportUi = null;

    const attachTransportUi = (t) => {
      const onReconnecting = () => {
        if (hubLobbySessionRef.current !== session) return;
        setHubSocketPhase(HUB_SOCKET_PHASE.RECONNECTING);
      };
      const onReconnected = () => {
        if (hubLobbySessionRef.current !== session) return;
        setHubSocketPhase(HUB_SOCKET_PHASE.ONLINE);
      };
      const onDisconnect = (payload) => {
        if (hubLobbySessionRef.current !== session) return;
        if (payload?.intentional) return;
        setHubSocketPhase(HUB_SOCKET_PHASE.OFFLINE);
      };
      t.on('reconnecting', onReconnecting);
      t.on('reconnected', onReconnected);
      t.on('disconnect', onDisconnect);
      return () => {
        t.off('reconnecting', onReconnecting);
        t.off('reconnected', onReconnected);
        t.off('disconnect', onDisconnect);
      };
    };

    (async () => {
      try {
        transport = await ColyseusLobbyTransport.connect(user, sessionToken || undefined);
        if (hubLobbySessionRef.current !== session) {
          transport.disconnect();
          return;
        }
        transport.userId = user.id;
        transport.username = user.username;
        detachTransportUi = attachTransportUi(transport);
        setSocket(transport);
        setHubSocketPhase(HUB_SOCKET_PHASE.ONLINE);
        setError(null);
      } catch (err) {
        console.error('Ошибка подключения к Colyseus lobby:', err);
        if (hubLobbySessionRef.current === session) {
          setError('Не удалось подключиться к Colyseus. Запустите сервер (порт 3334).');
          setHubSocketPhase(HUB_SOCKET_PHASE.OFFLINE);
        }
      }
    })();

    return () => {
      hubLobbySessionRef.current += 1;
      if (typeof detachTransportUi === 'function') detachTransportUi();
      if (transport) {
        try {
          transport.disconnect();
        } catch {
          /* ignore */
        }
      }
    };
  }, [user, sessionToken, currentView]);

  useEffect(() => {
    if (!POKER_LOBBY_TEMP_DISABLED) return;
    if (currentView === 'poker-lobby' || currentView === 'poker-practice') {
      setCurrentView('lobby');
      setSuccess('Режим покера временно в разработке.');
      return;
    }
    if (currentView === 'online-lobby' && onlineLobbyOrigin === 'poker-lobby') {
      setOnlineLobbyOrigin('lobby');
    }
  }, [currentView, onlineLobbyOrigin]);

  // Очистка сообщений
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    try {
      localStorage.setItem(GLOBAL_LOBBY_CLAN_CHAT_KEY, JSON.stringify(clanLobbyMessages.slice(-80)));
    } catch {
      /* ignore */
    }
  }, [clanLobbyMessages]);

  useEffect(() => {
    if (sessionToken) return;
    try {
      localStorage.setItem(GLOBAL_LOBBY_CHAT_MODERATION_KEY, JSON.stringify(globalChatModeration));
    } catch {
      // ignore quota/private mode
    }
  }, [globalChatModeration, sessionToken]);

  useEffect(() => {
    if (sessionToken) return;
    try {
      localStorage.setItem(GLOBAL_LOBBY_INBOX_KEY, JSON.stringify(lobbyInbox.slice(-100)));
    } catch {
      // ignore quota/private mode
    }
  }, [lobbyInbox, sessionToken]);

  useEffect(() => {
    if (!sessionToken || !user?.id) return undefined;
    void refreshLobbyInboxFromApi();
  }, [sessionToken, user?.id, refreshLobbyInboxFromApi]);

  useEffect(() => {
    if (!sessionToken || !user?.id) return undefined;
    const id = setInterval(() => {
      void refreshLobbyInboxFromApi();
    }, MAIL_INBOX_POLL_MS);
    return () => clearInterval(id);
  }, [sessionToken, user?.id, refreshLobbyInboxFromApi]);

  useEffect(() => {
    if (!sessionToken || !user?.id) return undefined;
    const onVis = () => {
      if (document.visibilityState === 'visible') void refreshLobbyInboxFromApi();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [sessionToken, user?.id, refreshLobbyInboxFromApi]);

  useEffect(() => {
    if (!sessionToken || !user) return;
    const w = Math.max(0, Math.floor(Number(user.globalChatWarnings) || 0));
    const b = Math.max(0, Number(user.globalChatBlockedUntil) || 0);
    setGlobalChatModeration((prev) => ({
      ...prev,
      warningCount: w,
      blockedUntil: b,
    }));
  }, [sessionToken, user?.globalChatWarnings, user?.globalChatBlockedUntil, user]);

  useEffect(() => {
    const timer = setInterval(() => setChatNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Проверка авторизации при загрузке (в dev тоже — восстановить баланс с API после F5)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      checkAuth(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only JWT; не перезапускать при смене `checkAuth`
  }, []);

  /** Аватар с устройства: подставляем в user, если с сервера не пришёл URL. */
  useEffect(() => {
    const uid = user?.id;
    const currentAvatar = String(user?.avatarUrl || '').trim();
    const isDefaultAvatar =
      !currentAvatar || currentAvatar === DEFAULT_GUEST_AVATAR_URL || currentAvatar === FALLBACK_GUEST_AVATAR_URL;
    if (!uid || !isDefaultAvatar) return;
    try {
      const raw = localStorage.getItem(avatarLocalStorageKey(uid));
      if (raw && typeof raw === 'string' && raw.startsWith('data:image')) {
        setUser((prev) => {
          if (!prev || prev.id !== uid) return prev;
          const prevAvatar = String(prev.avatarUrl || '').trim();
          const prevIsDefault =
            !prevAvatar || prevAvatar === DEFAULT_GUEST_AVATAR_URL || prevAvatar === FALLBACK_GUEST_AVATAR_URL;
          if (!prevIsDefault) return prev;
          return { ...prev, avatarUrl: raw };
        });
      }
    } catch {
      /* ignore quota / private mode */
    }
  }, [user?.id, user?.avatarUrl]);

  const tryOfferLoginCarousel = useCallback(async () => {
    try {
      loginCarouselFetchAbortRef.current?.abort();
      const ac = new AbortController();
      loginCarouselFetchAbortRef.current = ac;
      const { signal } = ac;
      const r = await fetch(`${API_BASE_URL}/api/config/login-carousel`, { signal });
      if (!r.ok) return;
      const data = await r.json();
      if (signal.aborted) return;
      const slides = Array.isArray(data?.slides) ? data.slides : [];
      const version = Number(data?.version) || 0;
      if (!slides.length) return;
      let ack = 0;
      try {
        ack = Number(localStorage.getItem(LOGIN_CAROUSEL_ACK_KEY)) || 0;
      } catch {
        /* ignore */
      }
      if (version <= ack) return;
      if (signal.aborted) return;
      setLoginCarouselSlides(slides);
      setLoginCarouselVersion(version);
      setLoginCarouselOpen(true);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      /* ignore */
    }
  }, []);

  const dismissLoginCarousel = useCallback(() => {
    setLoginCarouselOpen(false);
    setLoginCarouselSlides([]);
  }, []);

  useEffect(
    () => () => {
      loginCarouselFetchAbortRef.current?.abort();
    },
    [],
  );

  /** В dev без JWT — один раз предложить карусель при первом заходе в лобби. */
  useEffect(() => {
    if (!SKIP_LOGIN_IN_DEV || sessionToken || loading) return;
    if (currentView !== 'lobby') return;
    if (devLoginCarouselOfferedRef.current) return;
    devLoginCarouselOfferedRef.current = true;
    void tryOfferLoginCarousel();
  }, [loading, currentView, sessionToken, tryOfferLoginCarousel]);

  const checkAuth = async (token) => {
    const gen = ++authCheckGenRef.current;
    const isCurrent = () => gen === authCheckGenRef.current;
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!isCurrent()) return;

      if (response.ok) {
        const data = await response.json();
        if (!isCurrent()) return;
        setUser(normalizeUserAvatar(data.user));
        setSessionToken(token);
        setCurrentView('lobby');
        await Promise.all([
          (async () => {
            try {
              const ir = await fetch(`${API_BASE_URL}/api/mail/inbox`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (ir.ok) {
                const inboxData = await ir.json();
                const list = (inboxData.messages || []).map(normalizeInboxRow).filter(Boolean);
                setLobbyInbox(list);
              }
            } catch {
              /* ignore */
            }
          })(),
          (async () => {
            try {
              const fr = await fetch(`${API_BASE_URL}/api/friends/requests`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (!fr.ok) return;
              const frData = await fr.json();
              const list = Array.isArray(frData.requests) ? frData.requests : [];
              const uid = String(data.user?.id || '').trim();
              if (uid) setMailFriendRequests((prev) => mergeFriendRequestsLocalAndServer(prev, list, uid));
            } catch {
              /* ignore */
            }
          })(),
          tryOfferLoginCarousel(),
        ]);
      } else {
        const status = response.status;
        if (status === 401) {
          if (!isCurrent()) return;
          localStorage.removeItem('token');
          setSessionToken(null);
          setUser(SKIP_LOGIN_IN_DEV ? createDevUser() : null);
          if (!SKIP_LOGIN_IN_DEV) setCurrentView('auth');
        }
      }
    } catch (error) {
      console.error('Ошибка проверки авторизации:', error);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const refreshAuthUser = useCallback(async () => {
    if (!sessionToken) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUser(normalizeUserAvatar(data.user));
      }
    } catch {
      /* ignore */
    }
  }, [sessionToken]);

  const flushLocalSekaWalletDeltasToServer = useCallback(async () => {
    if (localSekaWalletDeltaTimerRef.current) {
      clearTimeout(localSekaWalletDeltaTimerRef.current);
      localSekaWalletDeltaTimerRef.current = null;
    }

    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    const sent = localSekaWalletDeltaPendingRef.current;
    if (sent === 0 || !token) return;

    if (!publicFlagsRef.current.sekaLocalClientWalletSyncEnabled) {
      return;
    }

    localSekaWalletDeltaPendingRef.current -= sent;

    const idempotencyKey = generateSekaLocalWalletIdempotencyKey();
    const limitsSnapshot = localSekaLimitsSnapshotRef.current;
    const fingerprint = getOrCreateSekaClientFingerprint();

    const body = { delta: sent, idempotencyKey };
    if (limitsSnapshot && typeof limitsSnapshot === 'object') {
      body.limitsSnapshot = limitsSnapshot;
    }

    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    if (fingerprint) headers['X-Client-Fingerprint'] = fingerprint;

    try {
      const r = await fetch(`${API_BASE_URL}/api/seka/local/wallet/delta`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        localSekaWalletDeltaPendingRef.current += sent;
        if (data?.code === 'SEKA_LOCAL_WALLET_SYNC_DISABLED') {
          setError(typeof data.error === 'string' ? data.error : 'Синхрон локального кошелька отключён.');
        } else if (data?.code === 'SEKA_LOCAL_POS_ROLLUP_EXCEEDED') {
          setError(typeof data.error === 'string' ? data.error : 'Превышен лимит начислений за час.');
        }
        await refreshAuthUser();
        return;
      }
      if (typeof data.balance === 'number' && Number.isFinite(data.balance)) {
        setUser((prev) => (prev ? { ...prev, balance: Math.max(0, Math.trunc(data.balance)) } : prev));
      }
    } catch {
      localSekaWalletDeltaPendingRef.current += sent;
      await refreshAuthUser();
    }
  }, [refreshAuthUser]);

  const queueLocalSekaWalletDelta = useCallback(
    (delta) => {
      const d = Math.trunc(Number(delta) || 0);
      if (!d) return;
      if (!publicFlagsRef.current.sekaLocalClientWalletSyncEnabled) return;
      localSekaWalletDeltaPendingRef.current += d;
      if (localSekaWalletDeltaTimerRef.current) clearTimeout(localSekaWalletDeltaTimerRef.current);
      localSekaWalletDeltaTimerRef.current = setTimeout(() => {
        localSekaWalletDeltaTimerRef.current = null;
        void flushLocalSekaWalletDeltasToServer();
      }, 380);
    },
    [flushLocalSekaWalletDeltasToServer]
  );

  const handleUserProfilePatch = useCallback(
    (patch) => {
      if (!patch || typeof patch !== 'object') return;
      const walletDeltaSource = patch.walletDeltaSource;
      const userPatch = { ...patch };
      delete userPatch.walletDeltaSource;
      delete userPatch.localSekaLimitsSnapshot;

      if (walletDeltaSource === 'local_seka' && patch.localSekaLimitsSnapshot && typeof patch.localSekaLimitsSnapshot === 'object') {
        const snap = patch.localSekaLimitsSnapshot;
        localSekaLimitsSnapshotRef.current = {
          botCount: Math.max(0, Math.trunc(Number(snap.botCount) || 0)),
          tableAnte: Math.max(0, Math.trunc(Number(snap.tableAnte) || 0)),
          startingStack: Math.max(1, Math.trunc(Number(snap.startingStack) || 0)),
        };
      }

      setUser((prev) => {
        if (!prev) return prev;
        if (
          typeof window !== 'undefined' &&
          localStorage.getItem('token') &&
          walletDeltaSource === 'local_seka' &&
          Object.prototype.hasOwnProperty.call(userPatch, 'balance')
        ) {
          const prevBal = Math.max(0, Math.floor(Number(prev.balance) || 0));
          const nextBal = Math.max(0, Math.floor(Number(userPatch.balance) || 0));
          const d = nextBal - prevBal;
          if (d !== 0) queueLocalSekaWalletDelta(d);
        }
        const next = { ...prev, ...userPatch };
        if (Object.prototype.hasOwnProperty.call(userPatch, 'avatarUrl') && prev.id) {
          try {
            if (userPatch.avatarUrl) {
              localStorage.setItem(avatarLocalStorageKey(prev.id), userPatch.avatarUrl);
            } else {
              localStorage.removeItem(avatarLocalStorageKey(prev.id));
            }
          } catch {
            /* ignore */
          }
        }
        return next;
      });
    },
    [queueLocalSekaWalletDelta]
  );

  const handleLogin = async (userData, token, options = {}) => {
    const isNewAccount = Boolean(options?.isNewAccount);
    const balance = Number(userData?.balance) || 0;
    const normalizedUser =
      isNewAccount && balance < NEW_ACCOUNT_FREE_COINS
        ? normalizeUserAvatar({ ...userData, balance: NEW_ACCOUNT_FREE_COINS })
        : normalizeUserAvatar(userData);
    setUser(normalizedUser);
    localStorage.setItem('token', token);
    setSessionToken(token);
    setCurrentView('lobby');
    setSuccess(isNewAccount ? `Аккаунт создан: +${NEW_ACCOUNT_FREE_COINS.toLocaleString('ru-RU')} монет` : 'Успешный вход в систему!');
    await Promise.all([
      (async () => {
        try {
          const ir = await fetch(`${API_BASE_URL}/api/mail/inbox`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (ir.ok) {
            const inboxData = await ir.json();
            const list = (inboxData.messages || []).map(normalizeInboxRow).filter(Boolean);
            setLobbyInbox(list);
          }
        } catch {
          /* ignore */
        }
      })(),
      (async () => {
        try {
          const fr = await fetch(`${API_BASE_URL}/api/friends/requests`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!fr.ok) return;
          const frData = await fr.json();
          const list = Array.isArray(frData.requests) ? frData.requests : [];
          const uid = String(normalizedUser?.id || '').trim();
          if (uid) setMailFriendRequests((prev) => mergeFriendRequestsLocalAndServer(prev, list, uid));
        } catch {
          /* ignore */
        }
      })(),
      tryOfferLoginCarousel(),
    ]);
  };

  const handleDailyRewardClaim = useCallback((payload, legacyReward) => {
    const obj = payload && typeof payload === 'object' && payload.message != null ? payload : null;
    if (obj) {
      setSuccess(String(obj.message || ''));
      const rewardValue = Number(obj.reward) || 0;
      if (typeof obj.balance === 'number' && Number.isFinite(obj.balance)) {
        setUser((prev) => (prev ? { ...prev, balance: obj.balance } : prev));
      } else if (rewardValue > 0) {
        setUser((prev) => {
          if (!prev) return prev;
          return { ...prev, balance: (Number(prev.balance) || 0) + rewardValue };
        });
      }
      if (obj.state) {
        setDailyRewardsHubClaimable(isDailyRewardClaimableFromServerState(obj.state));
      }
      return;
    }
    const message = typeof payload === 'string' ? payload : '';
    const rewardValue = Number(legacyReward) || 0;
    if (message) setSuccess(message);
    if (rewardValue <= 0) return;
    setUser((prev) => {
      if (!prev) return prev;
      return { ...prev, balance: (Number(prev.balance) || 0) + rewardValue };
    });
  }, []);

  const handleLogout = async () => {
    if (localSekaWalletDeltaTimerRef.current) {
      clearTimeout(localSekaWalletDeltaTimerRef.current);
      localSekaWalletDeltaTimerRef.current = null;
    }
    await flushLocalSekaWalletDeltasToServer();
    localSekaWalletDeltaPendingRef.current = 0;
    try {
      localStorage.removeItem('token');
    } catch {
      /* ignore */
    }
    loginCarouselFetchAbortRef.current?.abort();
    setLoginCarouselOpen(false);
    setLoginCarouselSlides([]);
    setSessionToken(null);
    setUser(null);
    setDailyRewardsHubClaimable(false);
    setCurrentView('auth');
    setLobbyInbox(loadLobbyInboxFromStorage() || DEFAULT_LOBBY_INBOX_NORMALIZED);
    if (socket) {
      socket.disconnect();
      setSocket(null);
    }
  };

  const fullBleed =
    (typeof currentView === 'string' && currentView.startsWith('game-')) ||
    currentView === 'online-lobby' ||
    currentView === 'local' ||
    currentView === 'mobile-local' ||
    currentView === 'poker-practice' ||
    currentView === 'belot-game';

  const showAppChrome = !fullBleed && currentView !== 'auth' && currentView !== 'profile';

  useEffect(() => {
    if (!showAppChrome || !publicFlags.dailyRewardsEnabled) {
      setDailyRewardsHubClaimable(false);
      return;
    }
    const token =
      sessionToken ||
      (typeof window !== 'undefined' && typeof localStorage !== 'undefined'
        ? localStorage.getItem('token')
        : null);
    if (!token) {
      setDailyRewardsHubClaimable(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        let tz = 'UTC';
        try {
          tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        } catch {
          /* ignore */
        }
        const res = await fetch(
          `${API_BASE_URL}/api/rewards/daily-state?timezone=${encodeURIComponent(tz)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !data?.state) {
          setDailyRewardsHubClaimable(false);
          return;
        }
        setDailyRewardsHubClaimable(isDailyRewardClaimableFromServerState(data.state));
      } catch {
        if (!cancelled) setDailyRewardsHubClaimable(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showAppChrome, publicFlags.dailyRewardsEnabled, sessionToken, dailyRewardsOpen]);

  /** Шапка приложения: покер/белот по контексту текущего режима. */
  const appChromePokerContext =
    currentView === 'poker-lobby' ||
    currentView === 'poker-practice' ||
    (currentView === 'online-lobby' && onlineLobbyOrigin === 'poker-lobby') ||
    (currentView === 'wallet' && walletReturnViewRef.current === 'poker-lobby');
  const appChromeBelotContext =
    currentView === 'belot' ||
    currentView === 'belot-game' ||
    (currentView === 'online-lobby' && onlineLobbyOrigin === 'belot') ||
    (currentView === 'wallet' && walletReturnViewRef.current === 'belot');

  const appChromeTitle = appChromePokerContext ? 'Покер' : appChromeBelotContext ? 'Белот' : 'Сека';
  const appChromeSubtitle = appChromePokerContext
    ? 'Texas Hold’em · практика и столы'
    : appChromeBelotContext
      ? 'Белот · классический режим'
      : 'Карточный стол онлайн';

  const dmThreadMessages = useMemo(() => {
    if (!dmSelectedPeerId) return [];
    const pid = String(dmSelectedPeerId);
    return dmLobbyMessages.filter((m) => String(m.peerId || '') === pid);
  }, [dmLobbyMessages, dmSelectedPeerId]);

  const hasHubClanChat = useMemo(() => {
    const t = String(lobbyUser?.clan || '').trim();
    return Boolean(t && t !== 'Без клана' && t !== 'SYSTEM');
  }, [lobbyUser?.clan]);

  const hubChatChannelDefs = useMemo(() => {
    const all = [
      { id: 'global', label: 'Глоб.', labelLg: 'Глобальный', Icon: Globe2 },
      { id: 'dm', label: 'Братва', labelLg: 'Малявы (братва)', Icon: Users },
      { id: 'clan', label: 'Клан', labelLg: 'Клан', Icon: Shield },
    ];
    return all.filter((c) => {
      // ЛС друзей должны быть всегда доступны в переключателе каналов.
      if (c.id === 'dm') return true;
      if (c.id === 'clan') return publicFlags.hubClanChatEnabled;
      return true;
    });
  }, [publicFlags.hubClanChatEnabled]);

  useEffect(() => {
    if (hubChatChannel === 'clan' && !publicFlags.hubClanChatEnabled) setHubChatChannel('global');
  }, [hubChatChannel, publicFlags.hubClanChatEnabled]);

  const clanLobbyDisplayRows = useMemo(
    () => (hasHubClanChat ? clanLobbyMessages : CLAN_CHAT_LOCKED_ROWS),
    [hasHubClanChat, clanLobbyMessages]
  );

  const hubActiveMessages = useMemo(() => {
    if (hubChatChannel === 'global') return globalLobbyMessages;
    if (hubChatChannel === 'dm') return dmThreadMessages;
    return clanLobbyDisplayRows;
  }, [hubChatChannel, globalLobbyMessages, dmThreadMessages, clanLobbyDisplayRows]);

  const clanChatCooldownLeftSec = Math.max(0, Math.ceil((HUB_AUX_CHAT_COOLDOWN_MS - (chatNowMs - hubClanLastSentAt)) / 1000));
  const isClanSendLocked = !hasHubClanChat || clanChatCooldownLeftSec > 0;
  const dmNavUnreadCount = useMemo(
    () =>
      Object.values(dmUnreadByPeer).reduce((sum, n) => {
        const next = Math.max(0, Number(n) || 0);
        return sum + next;
      }, 0),
    [dmUnreadByPeer]
  );

  const isMaintenanceBypassUser = useMemo(() => {
    const uid = lobbyUser?.id != null ? String(lobbyUser.id).trim() : '';
    if (!uid) return false;
    const list = publicMaintenance.allowedTesterUserIds;
    if (!Array.isArray(list) || !list.length) return false;
    const allow = new Set(list.map((x) => String(x).trim()).filter(Boolean));
    return allow.has(uid);
  }, [lobbyUser?.id, publicMaintenance.allowedTesterUserIds]);

  const shouldShowMaintenanceLock = useMemo(
    () =>
      publicMaintenance.enabled &&
      !isMaintenanceBypassUser &&
      !loading &&
      currentView !== 'auth' &&
      (Boolean(user) || (SKIP_LOGIN_IN_DEV && !sessionToken && Boolean(lobbyUser))),
    [
      publicMaintenance.enabled,
      isMaintenanceBypassUser,
      loading,
      currentView,
      user,
      sessionToken,
      lobbyUser,
    ],
  );

  /** Единая полоса ресурсов в шапке: лобби Секи, покер, белот. */
  const showHubResourceRail =
    currentView === 'lobby' || currentView === 'poker-lobby' || currentView === 'belot';
  const showHubTopActions = showHubResourceRail && !friendsOpen;

  /** Высота плавающей шапки (хаб с чатом / простой chrome) — для отступа контента и `calc(100dvh - …)` в лобби. */
  const appHeaderShellRef = useRef(null);
  const appMainScrollRef = useRef(null);
  const appHeaderMeasureRoundedRef = useRef(null);
  const [appHeaderMeasurePx, setAppHeaderMeasurePx] = useState(null);
  useLayoutEffect(() => {
    if (!showAppChrome) {
      appHeaderMeasureRoundedRef.current = null;
      setAppHeaderMeasurePx(null);
      return undefined;
    }
    const el = appHeaderShellRef.current;
    if (!el) return undefined;

    appHeaderMeasureRoundedRef.current = null;
    let rafId = 0;

    const apply = () => {
      const raw = el.getBoundingClientRect().height;
      const next = Math.max(0, Math.round(raw / 4) * 4);
      const prevRounded = appHeaderMeasureRoundedRef.current;
      if (prevRounded != null && Math.abs(prevRounded - next) < 4) return;
      appHeaderMeasureRoundedRef.current = next;
      setAppHeaderMeasurePx((prev) => (prev === next ? prev : next));
    };

    const schedule = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        apply();
      });
    };

    schedule();
    if (typeof ResizeObserver === 'undefined') {
      return () => cancelAnimationFrame(rafId);
    }
    const ro = new ResizeObserver(() => schedule());
    ro.observe(el);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [
    showAppChrome,
    showHubResourceRail,
    currentView,
    friendsOpen,
    globalChatCollapsed,
    hubChatChannel,
    publicFlags.globalChatEnabled,
  ]);

  useEffect(() => {
    if (!showAppChrome) return;
    const el = appMainScrollRef.current;
    if (!el) return;
    el.scrollTo({ top: 0, behavior: 'auto' });
  }, [showAppChrome, currentView]);

  const openWalletForResourceRail = useCallback(() => {
    if (currentView === 'poker-lobby') walletReturnViewRef.current = 'poker-lobby';
    else if (currentView === 'belot') walletReturnViewRef.current = 'belot';
    else walletReturnViewRef.current = 'lobby';
    setCurrentView('wallet');
  }, [currentView]);

  const openShopScreen = useCallback(() => {
    if (!publicFlags.shopEnabled) {
      setError('Магазин временно отключен.');
      return;
    }
    setShopOpen(true);
  }, [publicFlags.shopEnabled]);

  useEffect(() => {
    let cancelled = false;
    async function loadShopCatalog() {
      try {
        const response = await fetch(`${API_BASE_URL}/api/shop/catalog`);
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) {
          setShopCatalog(data?.catalog || null);
        }
      } catch {
        /* ignore shop catalog fetch errors */
      }
    }
    loadShopCatalog();
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshPublicConfig = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/config/public`);
      if (!response.ok) return;
      const data = await response.json();
      const flags = data?.flags || {};
      setPublicFlags((prev) => ({
        ...prev,
        shopEnabled: typeof flags.shopEnabled === 'boolean' ? flags.shopEnabled : prev.shopEnabled,
        shopPurchasesEnabled:
          typeof flags.shopPurchasesEnabled === 'boolean' ? flags.shopPurchasesEnabled : prev.shopPurchasesEnabled,
        globalChatEnabled:
          typeof flags.globalChatEnabled === 'boolean' ? flags.globalChatEnabled : prev.globalChatEnabled,
        dailyRewardsEnabled:
          typeof flags.dailyRewardsEnabled === 'boolean' ? flags.dailyRewardsEnabled : prev.dailyRewardsEnabled,
        mailEnabled: typeof flags.mailEnabled === 'boolean' ? flags.mailEnabled : prev.mailEnabled,
        hubDmChatEnabled:
          typeof flags.hubDmChatEnabled === 'boolean' ? flags.hubDmChatEnabled : prev.hubDmChatEnabled,
        hubClanChatEnabled:
          typeof flags.hubClanChatEnabled === 'boolean' ? flags.hubClanChatEnabled : prev.hubClanChatEnabled,
        friendGiftsScreenEnabled:
          typeof flags.friendGiftsScreenEnabled === 'boolean'
            ? flags.friendGiftsScreenEnabled
            : prev.friendGiftsScreenEnabled,
        sekaLocalClientWalletSyncEnabled:
          typeof flags.sekaLocalClientWalletSyncEnabled === 'boolean'
            ? flags.sekaLocalClientWalletSyncEnabled
            : prev.sekaLocalClientWalletSyncEnabled,
        sekaLocalSessionEconomyEnabled:
          typeof flags.sekaLocalSessionEconomyEnabled === 'boolean'
            ? flags.sekaLocalSessionEconomyEnabled
            : prev.sekaLocalSessionEconomyEnabled,
        stripeCheckoutConfigured:
          typeof data.stripeCheckoutConfigured === 'boolean'
            ? data.stripeCheckoutConfigured
            : prev.stripeCheckoutConfigured,
      }));
      const m = data?.maintenance;
      const rawIds = m?.allowedTesterUserIds;
      const allowedTesterUserIds = Array.isArray(rawIds)
        ? rawIds.map((x) => String(x).trim()).filter(Boolean).slice(0, 200)
        : [];
      setPublicMaintenance({
        enabled: Boolean(m?.enabled),
        message: typeof m?.message === 'string' ? m.message : '',
        retryAfterMinutes:
          m?.retryAfterMinutes != null && Number.isFinite(Number(m.retryAfterMinutes))
            ? Number(m.retryAfterMinutes)
            : null,
        allowedTesterUserIds,
      });
    } catch {
      /* ignore config fetch errors */
    }
  }, []);

  const handleShopPurchase = useCallback(
    (item) => {
      if (!publicFlags.shopPurchasesEnabled) {
        setError('Покупки отключены: каталог только для ознакомления.');
        return;
      }
      const label = item?.name || item?.id || 'товар';
      if (!publicFlags.stripeCheckoutConfigured) {
        setError(
          'Stripe не настроен на сервере: в файле server/.env укажите STRIPE_SECRET_KEY=sk_test_… (Dashboard → Developers → API keys) и перезапустите Node. Шаблон: server/.env.example.'
        );
        return;
      }
      if (!sessionToken) {
        setError(
          SKIP_LOGIN_IN_DEV
            ? 'Войдите в аккаунт (меню «Профиль»), чтобы открылась оплата Stripe: в демо-лобби без входа JWT не выдаётся.'
            : 'Войдите в аккаунт, чтобы оплатить.'
        );
        return;
      }
      const checkoutBody = resolveShopStripeCheckout(item);
      if (!checkoutBody) {
        if (item?.type === 'daily' || item?.type === 'hourly' || item?.type === 'video') {
          setError('Эту халяву забирай в «Бесплатные награды» или в «ежедневные ништяки».');
          return;
        }
        setSuccess(`«${label}» — отдельная оплата пока не подключена.`);
        return;
      }
      void (async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/shop/stripe/checkout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${sessionToken}`,
            },
            body: JSON.stringify(checkoutBody),
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            setError(typeof data.error === 'string' ? data.error : 'Не удалось начать оплату');
            return;
          }
          if (typeof data.url === 'string' && data.url) {
            window.location.assign(data.url);
            return;
          }
          setError('Stripe не вернул ссылку на оплату.');
        } catch {
          setError('Сеть: не удалось связаться с сервером оплаты.');
        }
      })();
    },
    [publicFlags.shopPurchasesEnabled, publicFlags.stripeCheckoutConfigured, sessionToken]
  );

  const shopReturnHandledRef = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined' || shopReturnHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const shop = params.get('shop');
    if (!shop) return;
    shopReturnHandledRef.current = true;
    params.delete('shop');
    params.delete('session_id');
    const qs = params.toString();
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash || ''}`
    );
    if (shop === 'success') {
      void refreshAuthUser();
      void refreshPublicConfig();
      setSuccess('Оплата прошла. Баланс обновится через несколько секунд после подтверждения Stripe.');
    } else if (shop === 'cancel') {
      setError('Оплата отменена.');
    }
  }, [refreshAuthUser, refreshPublicConfig]);

  useEffect(() => {
    void refreshPublicConfig();
  }, [refreshPublicConfig]);

  useEffect(() => {
    if (!shopOpen) return;
    void refreshPublicConfig();
  }, [shopOpen, refreshPublicConfig]);

  useEffect(() => {
    if (!publicMaintenance.enabled) return undefined;
    const id = setInterval(() => {
      void refreshPublicConfig();
    }, 20000);
    return () => clearInterval(id);
  }, [publicMaintenance.enabled, refreshPublicConfig]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') void refreshPublicConfig();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [refreshPublicConfig]);

  const openProfileFromResourceRail = useCallback(() => {
    if (currentView === 'poker-lobby') profileReturnViewRef.current = 'poker-lobby';
    else if (currentView === 'belot') profileReturnViewRef.current = 'belot';
    else profileReturnViewRef.current = 'lobby';
    setCurrentView('profile');
  }, [currentView]);

  const openFriendGiftsFromNav = useCallback(() => {
    setFriendGiftsOpen(true);
  }, []);

  const openFriendsScreen = useCallback(() => {
    setFriendsOpen(true);
    setMailFriendRequests((prev) =>
      prev.map((r) =>
        r.type === 'incoming' && r.status === 'pending' && !r.seen ? { ...r, seen: true } : r
      )
    );
  }, [setMailFriendRequests]);

  const openFriendGiftsFromFriendsScreen = useCallback(() => {
    setFriendsOpen(false);
    setFriendGiftsOpen(true);
  }, []);
  const openFriendDmFromFriendsScreen = useCallback(
    (friend) => {
      const peerId = String(friend?.id || '').trim();
      if (!peerId || peerId === 'system') {
        setError('Не удалось открыть личку: некорректный профиль.');
        return;
      }
      if (!hubDmFriendsList.some((f) => String(f.id) === peerId)) {
        setError('Личка доступна только с друзьями из списка «Братва».');
        return;
      }
      setFriendsOpen(false);
      setGlobalChatCollapsed(false);
      setHubChatChannel('dm');
      setDmSelectedPeerId(peerId);
      setSuccess(`Открыт ЛС с игроком ${friend?.username || 'Игрок'}`);
    },
    [hubDmFriendsList, setError]
  );

  const handleFriendGiftsBalanceUpdate = useCallback((balance) => {
    setUser((prev) => (prev ? { ...prev, balance: Number(balance) || 0 } : prev));
  }, []);

  const appendClanLobbyMessage = useCallback((senderLike, text) => {
    const safeText = String(text || '').trim();
    if (!safeText) return;
    const sender =
      typeof senderLike === 'string'
        ? {
            id: senderLike.toLowerCase().replace(/\s+/g, '-'),
            username: senderLike,
            clan: senderLike === 'Система' ? 'SYSTEM' : 'Без клана',
            level: senderLike === 'Система' ? 99 : 1,
            avatarUrl: null,
          }
        : {
            id: senderLike?.id || 'player',
            username: senderLike?.username || 'Игрок',
            clan: senderLike?.clan || 'Без клана',
            level: Number(senderLike?.level) || 1,
            avatarUrl: senderLike?.avatarUrl || null,
          };
    setClanLobbyMessages((prev) =>
      [
        ...prev,
        {
          id: `clan-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          sender,
          text: safeText,
          createdAt: Date.now(),
        },
      ].slice(-80)
    );
  }, []);

  const clearInviteExpireTimer = useCallback((inviteId) => {
    const id = String(inviteId || '').trim();
    if (!id) return;
    const t = inviteExpireTimersRef.current.get(id);
    if (t) clearTimeout(t);
    inviteExpireTimersRef.current.delete(id);
  }, []);

  const modeToOnlineLobbyOrigin = useCallback((mode) => {
    const normalized = String(mode || '').trim().toLowerCase();
    if (normalized === 'poker') return POKER_LOBBY_TEMP_DISABLED ? 'lobby' : 'poker-lobby';
    if (normalized === 'belot') return 'belot';
    return 'lobby';
  }, []);

  const routeToInviteWaitingRoom = useCallback(
    (inviteLike) => {
      const roomCode = String(inviteLike?.roomCode || '').trim().toUpperCase();
      if (!roomCode) return false;
      setOnlineLobbyOrigin(modeToOnlineLobbyOrigin(inviteLike?.mode));
      setOnlineLobbyHubOpenCreateModal(false);
      setOnlineLobbyHubPendingJoin({
        roomCode,
        isPrivate: Boolean(inviteLike?.isPrivate),
        inviteOnly: true,
        hubJoinNonce: Date.now(),
      });
      setCurrentView('online-lobby');
      return true;
    },
    [modeToOnlineLobbyOrigin]
  );

  const handleSocialInviteEvent = useCallback(
    (payload) => {
      const myId = String(lobbyUser?.id || '');
      const recipientUserId = String(payload?.recipientUserId || '');
      if (!myId) return;
      const inviteId = String(payload?.inviteId || '').trim();
      if (!inviteId) return;
      const status = String(payload?.status || '').toLowerCase();
      const from = payload?.from || {};
      const fromId = String(from?.id || '').trim();
      const isOutgoingMirror = fromId && fromId === myId;
      // Sender mirror events may transiently arrive without recipientUserId on some transports.
      if (recipientUserId && recipientUserId !== myId && !isOutgoingMirror) return;
      if (!recipientUserId && !isOutgoingMirror) return;
      const table = payload?.table || {};
      const baseRow = {
        id: inviteId,
        fromUserId: fromId,
        fromUsername: String(from?.username || 'Игрок'),
        fromAvatarUrl: from?.avatarUrl || null,
        toUserId: myId,
        roomCode: String(table?.roomCode || '').trim().toUpperCase(),
        gameRoomId: String(table?.gameRoomId || ''),
        isPrivate: Boolean(table?.isPrivate),
        mode: String(table?.mode || 'seka'),
        stake: Math.max(1, Math.floor(Number(table?.stake) || 0)),
        ante: Math.max(0, Math.floor(Number(table?.ante) || 0)),
        createdAt: Number(payload?.createdAt) || Date.now(),
        expiresAt: Number(payload?.expiresAt) || Date.now() + 10_000,
        decision: payload?.decision ? String(payload.decision) : null,
        status,
        read: false,
      };

      if (status === 'pending') {
        if (isOutgoingMirror) {
          setOutgoingTableInvites((prev) => [baseRow, ...prev.filter((x) => x.id !== inviteId)].slice(0, 50));
          if (!inviteSuccessByIdRef.current.has(inviteId)) {
            inviteSuccessByIdRef.current.add(inviteId);
            setSuccess(`Вызов отправлен: ${baseRow.fromUsername || 'корешу'}`);
          }
        } else {
          setIncomingTableInvites((prev) => [baseRow, ...prev.filter((x) => x.id !== inviteId)].slice(0, 50));
          if (String(currentView || '').startsWith('game-')) {
            setActiveGameInvite(baseRow);
          }
        }
        clearInviteExpireTimer(inviteId);
        const ttl = Math.max(200, baseRow.expiresAt - Date.now());
        const t = setTimeout(() => {
          setIncomingTableInvites((prev) => prev.filter((x) => x.id !== inviteId));
          setOutgoingTableInvites((prev) => prev.filter((x) => x.id !== inviteId));
          setActiveGameInvite((prev) => (prev?.id === inviteId ? null : prev));
          inviteExpireTimersRef.current.delete(inviteId);
        }, ttl);
        inviteExpireTimersRef.current.set(inviteId, t);
        return;
      }

      clearInviteExpireTimer(inviteId);
      setIncomingTableInvites((prev) =>
        prev.map((x) => (x.id === inviteId ? { ...x, status, decision: baseRow.decision, read: true } : x))
      );
      setOutgoingTableInvites((prev) =>
        prev.map((x) => (x.id === inviteId ? { ...x, status, decision: baseRow.decision, read: true } : x))
      );
      if (status === 'expired') {
        setIncomingTableInvites((prev) => prev.filter((x) => x.id !== inviteId));
        setOutgoingTableInvites((prev) => prev.filter((x) => x.id !== inviteId));
      }
      setActiveGameInvite((prev) => (prev?.id === inviteId ? null : prev));
      if (status === 'accepted' && String(baseRow.decision || '').toLowerCase() !== 'accept-after-hand') {
        if (!acceptedInviteAutoJoinRef.current.has(inviteId)) {
          acceptedInviteAutoJoinRef.current.add(inviteId);
          if (acceptedInviteAutoJoinRef.current.size > 300) {
            acceptedInviteAutoJoinRef.current.clear();
            acceptedInviteAutoJoinRef.current.add(inviteId);
          }
          const routed = routeToInviteWaitingRoom(baseRow);
          if (!routed) {
            setOnlineLobbyOrigin(modeToOnlineLobbyOrigin(baseRow.mode));
            setCurrentView('online-lobby');
          }
        }
      }
    },
    [
      clearInviteExpireTimer,
      currentView,
      lobbyUser?.id,
      modeToOnlineLobbyOrigin,
      routeToInviteWaitingRoom,
      setSuccess,
      setCurrentView,
    ]
  );

  useEffect(() => {
    if (!socket?.on) return undefined;
    const onAck = (payload) => {
      if (String(payload?.command || '') !== 'social/send-invite') return;
      const at = Number(pendingInviteToastRef.current.at || 0);
      if (!at || Date.now() - at > 12_000) return;
      const label = String(pendingInviteToastRef.current.targetLabel || '').trim() || 'корешу';
      setSuccess(`Вызов отправлен: ${label}`);
      pendingInviteToastRef.current = { at: 0, targetLabel: '' };
    };
    const handler = (payload) => {
      handleSocialInviteEvent(payload);
    };
    socket.on('ack', onAck);
    socket.on('social_invite', handler);
    return () => {
      socket.off('ack', onAck);
      socket.off('social_invite', handler);
    };
  }, [socket, handleSocialInviteEvent, setSuccess]);

  useEffect(
    () => () => {
      inviteExpireTimersRef.current.forEach((t) => clearTimeout(t));
      inviteExpireTimersRef.current.clear();
    },
    []
  );

  useEffect(() => {
    if (!socket?.on) return undefined;
    const onLobbyErr = (payload) => {
      const code = String(payload?.code || '');
      if (code === 'RATE_LIMITED' || code === 'BAD_PAYLOAD' || code === 'FORBIDDEN') {
        const msg = typeof payload?.message === 'string' && payload.message.trim() ? payload.message.trim() : null;
        if (msg) setError(msg);
        const d = payload?.details;
        if (d && (d.globalChatBlockedUntil != null || d.globalChatWarnings != null)) {
          setGlobalChatModeration((prev) => ({
            ...prev,
            blockedUntil: Math.max(Number(prev.blockedUntil) || 0, Number(d.globalChatBlockedUntil) || 0),
            warningCount: Math.max(Number(prev.warningCount) || 0, Math.floor(Number(d.globalChatWarnings) || 0)),
          }));
        }
      }
    };
    socket.on('error', onLobbyErr);
    return () => {
      socket.off('error', onLobbyErr);
    };
  }, [socket, setError, setGlobalChatModeration]);

  const handleSendFriendRequestFromChatProfile = useCallback(() => {
    if (!chatProfileUser) return;
    void (async () => {
      const result = await sendFriendRequestToProfileUser(chatProfileUser);
      if (result === 'sent' || result === 'resent') setChatProfileUser(null);
    })();
  }, [chatProfileUser, sendFriendRequestToProfileUser]);

  const handleMailRequestAction = useCallback(
    async (requestId, nextStatus, notifySenderUserId) => {
      const sid = String(notifySenderUserId || '').trim();
      const rid = String(requestId || '');
      if (
        sessionToken &&
        rid.startsWith('fr-srv-') &&
        (nextStatus === 'accepted' || nextStatus === 'declined')
      ) {
        try {
          const r = await fetch(`${API_BASE_URL}/api/friends/requests/${encodeURIComponent(rid)}/resolve`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${sessionToken}`,
            },
            body: JSON.stringify({ status: nextStatus }),
          });
          if (!r.ok) {
            const err = await r.json().catch(() => ({}));
            setError(typeof err.error === 'string' && err.error.trim() ? err.error.trim() : 'Не удалось обработать заявку');
            return;
          }
        } catch {
          setError('Сеть: не удалось обработать заявку');
          return;
        }
      }
      setMailFriendRequests((prev) =>
        prev.map((r) => (r.id === requestId ? { ...r, status: nextStatus, seen: true } : r))
      );
      if (sid && (nextStatus === 'accepted' || nextStatus === 'declined')) {
        emitFriendRequestResolvedToServer(sid, nextStatus);
      }
      if (nextStatus === 'accepted') setSuccess('Запрос закорефаниться принят');
      if (nextStatus === 'declined') setSuccess('Заявка отклонена');
    },
    [emitFriendRequestResolvedToServer, sessionToken, setError, setSuccess, setMailFriendRequests]
  );

  const sendGlobalLobbyChatMessage = useCallback(() => {
    const now = Date.now();
    const blockedLeftMs = (globalChatModeration.blockedUntil || 0) - now;
    if (blockedLeftMs > 0) {
      const mins = Math.ceil(blockedLeftMs / 60000);
      setError(`Чат заблокирован из-за нарушений. Осталось ~${mins} мин.`);
      return;
    }

    const cooldownLeftMs = CHAT_COOLDOWN_MS - (now - (globalChatModeration.lastSentAt || 0));
    if (cooldownLeftMs > 0) {
      const secs = Math.ceil(cooldownLeftMs / 1000);
      setError(`Отправлять можно не чаще 1 раза в минуту. Подожди ${secs} сек.`);
      return;
    }

    const txt = globalChatDraft.trim().slice(0, 220);
    if (!txt) return;

    if (!sessionToken && hasBadContent(txt)) {
      const nextWarnings = (globalChatModeration.warningCount || 0) + 1;
      if (nextWarnings >= MAX_CHAT_WARNINGS) {
        setGlobalChatModeration({
          warningCount: nextWarnings,
          blockedUntil: now + CHAT_BAN_MS,
          lastSentAt: globalChatModeration.lastSentAt || 0,
        });
        setError('Вы заблокированы в чате на 24 часа за токсичный контент.');
        return;
      }
      setGlobalChatModeration((prev) => ({ ...prev, warningCount: nextWarnings }));
      setError(`Нецензурный контент запрещен. Предупреждение ${nextWarnings}/${MAX_CHAT_WARNINGS}.`);
      return;
    }

    if (!socket?.emit || typeof socket.emit !== 'function') {
      setError('Глобальный чат недоступен: нет подключения к лобби (Colyseus).');
      return;
    }
    socket.emit('GLOBAL_LOBBY_CHAT', { text: txt });
    setGlobalChatModeration((prev) => ({ ...prev, lastSentAt: now }));
    setGlobalChatDraft('');
  }, [globalChatDraft, globalChatModeration, sessionToken, setError, socket]);

  const sendAuxHubChatMessage = useCallback(() => {
    if (!lobbyUser) return;
    const now = Date.now();
    if (hubChatChannel === 'dm') {
      const txt = dmChatDraft.trim().slice(0, 220);
      if (!txt) return;
      const sel = String(dmSelectedPeerId || '').trim();
      if (!sel) {
        setError('Выберите кореша слева.');
        return;
      }
      if (!hubDmFriendsList.some((f) => String(f.id) === sel)) {
        setError('Этот контакт недоступен для личных сообщений.');
        return;
      }
      if (hasBadContent(txt)) {
        setError('Сообщение содержит недопустимый контент.');
        return;
      }
      if (!socket?.emit || typeof socket.emit !== 'function') {
        setError('ЛС недоступны: нет подключения к лобби.');
        return;
      }
      socket.emit('friend_dm_send', { toUserId: sel, text: txt });
      setDmChatDraft('');
      return;
    }
    if (hubChatChannel === 'clan') {
      const tag = String(lobbyUser?.clan || '').trim();
      if (!tag || tag === 'Без клана' || tag === 'SYSTEM') {
        setError('Клановый чат доступен после вступления в клан.');
        return;
      }
      const cooldownLeftMs = HUB_AUX_CHAT_COOLDOWN_MS - (now - (hubClanLastSentAt || 0));
      if (cooldownLeftMs > 0) {
        setError(`Клан: следующее сообщение через ${Math.ceil(cooldownLeftMs / 1000)} сек.`);
        return;
      }
      const txt = clanChatDraft.trim().slice(0, 220);
      if (!txt) return;
      if (hasBadContent(txt)) {
        setError('Сообщение содержит недопустимый контент.');
        return;
      }
      appendClanLobbyMessage(senderFromCurrentUser(lobbyUser), txt);
      setClanChatDraft('');
      setHubClanLastSentAt(now);
    }
  }, [
    hubChatChannel,
    lobbyUser,
    dmChatDraft,
    clanChatDraft,
    hubClanLastSentAt,
    socket,
    appendClanLobbyMessage,
    dmSelectedPeerId,
    hubDmFriendsList,
  ]);

  useEffect(() => {
    if (hubChatChannel !== 'dm') return;
    if (!hubDmFriendsList.length) {
      setDmSelectedPeerId(null);
      return;
    }
    if (dmSelectedPeerId && hubDmFriendsList.some((f) => String(f.id) === String(dmSelectedPeerId))) return;
    setDmSelectedPeerId(String(hubDmFriendsList[0].id));
  }, [hubChatChannel, hubDmFriendsList, dmSelectedPeerId]);

  useEffect(() => {
    const peerId = String(dmSelectedPeerId || '').trim();
    if (!peerId) return;
    setDmUnreadByPeer((prev) => {
      if (!prev[peerId]) return prev;
      return { ...prev, [peerId]: 0 };
    });
    if (hubChatChannel !== 'dm' || !socket?.emit || typeof socket.emit !== 'function') return;
    socket.emit('friend_dm_mark_read', { peerUserId: peerId });
    if (!sessionToken) return;
    void fetch(`${API_BASE_URL}/api/friends/dm/thread/${encodeURIComponent(peerId)}/read`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${sessionToken}` },
    }).catch(() => {});
  }, [dmSelectedPeerId, hubChatChannel, socket, sessionToken, setDmUnreadByPeer]);

  const globalChatBlockedLeftMs = (globalChatModeration.blockedUntil || 0) - chatNowMs;
  const globalChatCooldownLeftMs = CHAT_COOLDOWN_MS - (chatNowMs - (globalChatModeration.lastSentAt || 0));
  const isGlobalChatBlocked = globalChatBlockedLeftMs > 0;
  const isGlobalChatCooldown = !isGlobalChatBlocked && globalChatCooldownLeftMs > 0;
  const isGlobalChatSendLocked = isGlobalChatBlocked || isGlobalChatCooldown;
  const globalChatCooldownLeftSec = Math.max(0, Math.ceil(globalChatCooldownLeftMs / 1000));
  const globalChatBlockedLeftMin = Math.max(0, Math.ceil(globalChatBlockedLeftMs / 60000));

  const globalChatHeaderPreview = useMemo(() => globalLobbyMessages.slice(-2), [globalLobbyMessages]);

  useEffect(() => {
    if (globalChatCollapsed) return;
    const el = globalChatScrollRef.current;
    if (!el) return;
    const run = () => {
      el.scrollTop = el.scrollHeight;
    };
    const id = window.requestAnimationFrame(run);
    return () => window.cancelAnimationFrame(id);
  }, [
    globalChatCollapsed,
    hubChatChannel,
    globalLobbyMessages,
    dmLobbyMessages,
    dmThreadMessages,
    dmSelectedPeerId,
    clanLobbyMessages,
    clanLobbyDisplayRows,
  ]);

  const chatProfileFriendship = useMemo(() => {
    if (!chatProfileUser?.id || !lobbyUser?.id) return { kind: 'hidden' };
    const pid = String(chatProfileUser.id);
    const mid = String(lobbyUser.id);
    if (pid === mid || pid === 'system') return { kind: 'self_or_system' };

    const accepted = mailFriendRequests.find(
      (r) =>
        r.status === 'accepted' &&
        ((String(r.from?.id) === mid && String(r.to?.id) === pid) ||
          (String(r.from?.id) === pid && String(r.to?.id) === mid))
    );
    if (accepted) return { kind: 'friends' };

    const incoming = mailFriendRequests.find(
      (r) =>
        r.status === 'pending' &&
        r.type === 'incoming' &&
        String(r.from?.id) === pid &&
        String(r.to?.id) === mid
    );
    if (incoming) return { kind: 'incoming_pending' };

    const outgoing = mailFriendRequests.find(
      (r) =>
        r.status === 'pending' &&
        r.type === 'outgoing' &&
        String(r.from?.id) === mid &&
        String(r.to?.id) === pid
    );
    if (outgoing) {
      const elapsed = chatNowMs - (Number(outgoing.createdAt) || 0);
      const resendLeftMs = Math.max(0, FRIEND_REQUEST_RESEND_COOLDOWN_MS - elapsed);
      return { kind: 'outgoing_pending', resendLeftMs };
    }

    return { kind: 'none' };
  }, [chatProfileUser, lobbyUser, mailFriendRequests, chatNowMs]);

  const friendsNavPendingRequests = mailFriendRequests.filter(
    (r) => r.type === 'incoming' && r.status === 'pending' && !r.seen
  );
  const friendsNavPendingInvites = incomingTableInvites.filter(
    (x) => x && String(x.status || '').toLowerCase() === 'pending' && x.read !== true
  );
  const friendsNavBadgeCount = friendsNavPendingRequests.length + friendsNavPendingInvites.length;

  const refreshFriendGiftsBadge = useCallback(async () => {
    if (!sessionToken) {
      setGiftsNavBadgeCount(0);
      return;
    }
    try {
      const resp = await fetch(`${API_BASE_URL}/api/friends/gifts/state`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      const ctype = String(resp.headers.get('content-type') || '').toLowerCase();
      if (!ctype.includes('application/json')) return;
      const data = await resp.json();
      if (!resp.ok) return;
      const incoming = Array.isArray(data?.incoming) ? data.incoming : [];
      const pending = incoming.filter((g) => !g?.claimedAt).length;
      setGiftsNavBadgeCount(pending);
    } catch {
      /* ignore */
    }
  }, [sessionToken]);

  useEffect(() => {
    void refreshFriendGiftsBadge();
  }, [refreshFriendGiftsBadge]);

  useEffect(() => {
    if (!sessionToken) return undefined;
    const id = setInterval(() => {
      void refreshFriendGiftsBadge();
    }, 30000);
    return () => clearInterval(id);
  }, [sessionToken, refreshFriendGiftsBadge]);

  /** Принятые заявки в друзья — единственный источник списка «Пригласить друга» в OnlineLobby. */
  const acceptedFriendsForInvite = useMemo(() => {
    const myId = String(lobbyUser?.id || '');
    if (!myId) return [];
    const seen = new Set();
    const out = [];
    for (const r of mailFriendRequests) {
      if (!r || r.status !== 'accepted' || !r.from || !r.to) continue;
      const fromId = String(r.from.id || '');
      const toId = String(r.to.id || '');
      const peer = fromId === myId ? r.to : toId === myId ? r.from : null;
      if (!peer || !peer.id) continue;
      const pid = String(peer.id);
      if (seen.has(pid)) continue;
      seen.add(pid);
      out.push({
        id: pid,
        username: String(peer.username || 'Игрок').trim() || 'Игрок',
        level: Math.max(1, Math.floor(Number(peer.level) || 1)),
        avatarUrl: peer.avatarUrl ? String(peer.avatarUrl).trim() : '',
        clan: String(peer.clan || ''),
      });
    }
    out.sort((a, b) => String(a.username).localeCompare(String(b.username), 'ru'));
    return out;
  }, [lobbyUser?.id, mailFriendRequests]);

  /** Друзья, недавно писавшие в глобальный чат — для бейджа «Онлайн» и сортировки сверху. */
  const liveLobbyUsersForFriendInvite = useMemo(() => {
    const friendIds = new Set(acceptedFriendsForInvite.map((f) => String(f.id)));
    if (!friendIds.size) return [];
    const windowMs = 12 * 60 * 1000;
    const now = chatNowMs;
    const byId = new Map();
    for (let i = globalLobbyMessages.length - 1; i >= 0; i -= 1) {
      const m = globalLobbyMessages[i];
      const sid = String(m?.sender?.id || '');
      if (!sid || sid === 'system' || !friendIds.has(sid)) continue;
      if (now - (Number(m.createdAt) || 0) > windowMs) continue;
      byId.set(sid, {
        id: sid,
        username: String(m?.sender?.username || '').trim(),
        lastPlayedLabel: 'В глобальном чате',
      });
    }
    return Array.from(byId.values());
  }, [acceptedFriendsForInvite, chatNowMs, globalLobbyMessages]);

  const unreadInboxCount = lobbyInbox.filter((m) => !m.read).length;
  const mailUnreadCount = publicFlags.mailEnabled ? unreadInboxCount : 0;

  const inboxKindLabel = (kind) => {
    if (kind === 'system') return 'Система';
    if (kind === 'broadcast') return 'Прогон от главного вора на централе';
    if (kind === 'notification') return 'Уведомление';
    if (kind === 'user') return 'От игрока';
    return 'Прогон';
  };

  const isVorInboxMessage = (item) => {
    const kind = String(item?.kind || '').toLowerCase();
    if (kind === 'system' || kind === 'broadcast' || kind === 'notification') return true;
    const senderId = String(item?.sender?.id || '').toLowerCase();
    if (senderId === 'system') return true;
    const from = String(item?.fromUsername || '').toLowerCase();
    return /(админ|сервер|system|система)/i.test(from);
  };

  const markInboxAllRead = useCallback(() => {
    setLobbyInbox((prev) => prev.map((m) => (m.read ? m : { ...m, read: true })));
  }, []);

  const openMailAndMarkRead = useCallback(async () => {
    setMailOpen(true);
    if (sessionToken) {
      try {
        await refreshLobbyInboxFromApi();
        const r = await fetch(`${API_BASE_URL}/api/mail/read-all`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (r.ok) {
          const data = await r.json();
          setLobbyInbox((data.messages || []).map(normalizeInboxRow).filter(Boolean));
          return;
        }
      } catch {
        /* fall through */
      }
    }
    markInboxAllRead();
  }, [sessionToken, markInboxAllRead, refreshLobbyInboxFromApi]);

  const handleClaimInboxReward = useCallback(
    async (messageId) => {
      if (!sessionToken || !messageId) return;
      try {
        const r = await fetch(`${API_BASE_URL}/api/mail/claim`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ messageId: String(messageId) }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) {
          setError(typeof data.error === 'string' ? data.error : 'Ништяк не забрался — глянь ещё раз');
          return;
        }
        if (data.user) {
          setUser((prev) => (prev ? { ...prev, balance: data.user.balance } : prev));
        }
        if (data.message) {
          const patch = normalizeInboxRow(data.message);
          setLobbyInbox((prev) =>
            prev.map((m) => (String(m.id) === String(messageId) && patch ? { ...m, ...patch } : m))
          );
        }
        const coins = Number(data.rewardCoins) || 0;
        if (coins > 0) {
          setSuccess(
            `Ништяк забран, чифирнул: +${coins.toLocaleString('ru-RU')} монет`
          );
        }
      } catch {
        setError('Сеть подвела — ништяк не доехал, попробуй снова');
      }
    },
    [sessionToken]
  );

  const sortedLobbyInbox = useMemo(
    () => [...lobbyInbox].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)),
    [lobbyInbox]
  );

  const openHubJoinInOnlineLobby = useCallback(({ roomCode, isPrivate, origin, roomPassword }) => {
    const o =
      origin === 'poker-lobby' ? 'poker-lobby' : origin === 'belot' ? 'belot' : 'lobby';
    setOnlineLobbyOrigin(o);
    setOnlineLobbyHubOpenCreateModal(false);
    const pw = String(roomPassword || '').trim();
    setOnlineLobbyHubPendingJoin({
      roomCode: String(roomCode || '')
        .trim()
        .toUpperCase(),
      isPrivate: Boolean(isPrivate),
      hubJoinNonce: Date.now(),
      ...(isPrivate && pw ? { roomPassword: pw } : {}),
    });
    setCurrentView('online-lobby');
  }, []);

  const emitSocialInviteCommand = useCallback(
    (kind, payload, options = {}) => {
      const source = String(options?.source || '').toLowerCase();
      const viaSocket = socket?.emit && typeof socket.emit === 'function';
      if (source === 'game') {
        return false;
      }
      if (viaSocket) {
        socket.emit(kind === 'resolve' ? 'social_resolve_invite' : 'social_send_invite', payload);
        return true;
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent(kind === 'resolve' ? 'seka-social-resolve-invite' : 'seka-social-send-invite', {
            detail: payload,
          })
        );
        return true;
      }
      return false;
    },
    [socket]
  );

  const sendFriendGameChallenge = useCallback(
    async ({ friend, mode, stake, ante }) => {
      const targetUserId = String(friend?.id || '').trim();
      if (!targetUserId) throw new Error('Не найден ID кореша для вызова');
      const invitePayload = {
        targetUserId,
        mode: String(mode || 'seka'),
        stake: Math.max(1, Math.floor(Number(stake) || 0)),
        ante: Math.max(0, Math.floor(Number(ante) || 0)),
        roomCode: '',
        gameRoomId: String(gameJoin?.gameRoomId || ''),
      };
      const ok = emitSocialInviteCommand('send', invitePayload);
      if (!ok) {
        throw new Error('Нет активного подключения к лобби для отправки вызова');
      }
      pendingInviteToastRef.current = {
        at: Date.now(),
        targetLabel: String(friend?.username || targetUserId || 'корешу'),
      };
    },
    [emitSocialInviteCommand, gameJoin]
  );

  const respondToTableInvite = useCallback(
    (invite, decision, options = {}) => {
      const inviteId = String(invite?.id || invite?.inviteId || '').trim();
      if (!inviteId) return;
      const normalizedDecision = String(decision || '').trim().toLowerCase();
      const shouldEmitViaLobbySocket = String(options?.source || '') !== 'game';
      if (shouldEmitViaLobbySocket) {
        emitSocialInviteCommand(
          'resolve',
          {
            inviteId,
            decision: normalizedDecision,
          },
          options
        );
      }
      if (normalizedDecision === 'accept-now') {
        // Route only after server confirms `accepted` to avoid stale/expired joins.
      }
      setIncomingTableInvites((prev) => prev.filter((x) => x.id !== inviteId));
      setActiveGameInvite((prev) => (prev?.id === inviteId ? null : prev));
      clearInviteExpireTimer(inviteId);
    },
    [emitSocialInviteCommand, clearInviteExpireTimer]
  );

  const globalInviteOverlayInvite = useMemo(() => {
    const pendingUnread = incomingTableInvites.find(
      (x) => x && String(x.status || '').toLowerCase() === 'pending' && x.read !== true
    );
    if (pendingUnread) return pendingUnread;
    if (activeGameInvite && String(activeGameInvite.status || '').toLowerCase() === 'pending') {
      return activeGameInvite;
    }
    return null;
  }, [incomingTableInvites, activeGameInvite]);

  const clearOnlineLobbyHubPendingJoin = useCallback(() => {
    setOnlineLobbyHubPendingJoin(null);
  }, []);

  const clearOnlineLobbyHubOpenCreateModal = useCallback(() => {
    setOnlineLobbyHubOpenCreateModal(false);
  }, []);
  const [onlineReconnectWaitContext, setOnlineReconnectWaitContext] = useState(null);

  const openOnlineLobbyWithCreateModal = useCallback((origin) => {
    const o =
      origin === 'poker-lobby' ? 'poker-lobby' : origin === 'belot' ? 'belot' : 'lobby';
    setOnlineLobbyOrigin(o);
    setOnlineLobbyHubPendingJoin(null);
    setOnlineLobbyHubOpenCreateModal(true);
    setCurrentView('online-lobby');
  }, []);

  const navigateToGameTable = useCallback((payload) => {
    const defaultReturnForMode = (mode) => {
      const m = String(mode || 'seka');
      if (m === 'poker') return 'poker-lobby';
      if (m === 'belot') return 'belot';
      return 'lobby';
    };
    const p =
      typeof payload === 'string'
        ? {
            tableId: String(payload || '').trim().toUpperCase(),
            gameRoomId: '',
            mode: 'seka',
            returnView: 'lobby',
          }
        : {
            tableId: String(payload?.tableId ?? '').trim().toUpperCase(),
            gameRoomId: String(payload?.gameRoomId ?? ''),
            mode: String(payload?.mode || 'seka'),
            returnView:
              payload?.returnView != null
                ? String(payload.returnView)
                : defaultReturnForMode(payload?.mode),
          };
    if (!p.tableId) return;
    const tableId = String(p.tableId || '').trim().toUpperCase();
    const incomingGameRoomId = String(p.gameRoomId || '').trim();
    const previousGameRoomId = String(gameTransitionByTableRef.current.get(tableId) || '').trim();
    if (previousGameRoomId && !incomingGameRoomId) return;
    if (previousGameRoomId && incomingGameRoomId && previousGameRoomId !== incomingGameRoomId) {
      console.warn('[App] conflicting gameRoomId ignored', { tableId, previousGameRoomId, incomingGameRoomId });
      return;
    }
    const activeJoin = gameJoinRef.current;
    const activeTableId = String(activeJoin?.tableId || '').trim().toUpperCase();
    const activeGameRoomId = String(activeJoin?.gameRoomId || '').trim();
    if (activeTableId === tableId && activeGameRoomId && !incomingGameRoomId) {
      return;
    }
    const resolvedGameRoomId = previousGameRoomId || incomingGameRoomId;
    const mode = String(p.mode || 'seka');
    const requiresExplicitGameRoomId = mode === 'poker' || mode === 'belot';
    if (requiresExplicitGameRoomId && !resolvedGameRoomId) {
      // 4p race: ранний GAME_START/апдейт может прийти без gameRoomId.
      // Ждём первый валидный gid и не рвём переход в лобби.
      return;
    }
    const startSignature = `${tableId}:${resolvedGameRoomId || ''}`;
    if (gameStartSignatureByTableRef.current.get(tableId) === startSignature) {
      return;
    }
    gameStartSignatureByTableRef.current.set(tableId, startSignature);
    if (resolvedGameRoomId) {
      gameTransitionByTableRef.current.set(tableId, resolvedGameRoomId);
    }
    setOnlineReconnectWaitContext(null);
    setGameJoin({
      ...p,
      tableId,
      gameRoomId: resolvedGameRoomId,
    });
    setCurrentView(`game-${tableId}`);
  }, []);

  /** Стабильная ссылка: иначе каждый рендер `game-*` создаёт новый `onBack` → BaseGameRoomScreen рвёт Colyseus join. */
  const handleGameBackFromRoom = useCallback((opts) => {
    const resolved = gameJoinRef.current;
    if (!resolved) {
      setCurrentView('lobby');
      return;
    }
    const back =
      opts && typeof opts === 'object' && String(opts.targetView || '').trim() === 'lobby'
        ? 'lobby'
        : resolved.returnView || 'lobby';
    const activeTableId = String(resolved.tableId || '').trim().toUpperCase();
    if (activeTableId) {
      gameTransitionByTableRef.current.delete(activeTableId);
      gameStartSignatureByTableRef.current.delete(activeTableId);
    }
    setGameJoin(null);
    setCurrentView(back);
  }, []);

  const handleOnlineMatchAborted = useCallback((detail) => {
    void refreshAuthUser();
    setOnlineReconnectWaitContext(null);
    const tableId = String(gameJoinRef.current?.tableId || '').trim().toUpperCase();
    if (tableId) {
      gameTransitionByTableRef.current.delete(tableId);
      gameStartSignatureByTableRef.current.delete(tableId);
    }
    setGameJoin(null);
    const v =
      detail && typeof detail.returnView === 'string' && detail.returnView.trim()
        ? detail.returnView.trim()
        : 'online-lobby';
    setCurrentView(v);
    const name =
      detail?.payload?.leaver?.username != null ? String(detail.payload.leaver.username) : 'Игрок';
    setSuccess(`Партия отменена: ${name} вышел. Баланс обновлён.`);
  }, [refreshAuthUser]);
  const handleOnlineReconnectWait = useCallback((detail) => {
    const payload = detail?.payload && typeof detail.payload === 'object' ? detail.payload : {};
    const kind = String(payload?.kind || '').trim().toLowerCase();
    if (kind === 'resumed') {
      setOnlineReconnectWaitContext(null);
      return;
    }
    const roomId = String(payload?.roomId || '');
    setOnlineReconnectWaitContext({
      roomId,
      reconnectWaitUserId: String(payload?.reconnectWaitUserId || payload?.userId || ''),
      reconnectWaitDeadlineAtMs: Number(payload?.reconnectWaitDeadlineAtMs) || null,
      reconnectWaitReason: String(payload?.reconnectWaitReason || 'connection_lost'),
      kind: kind || 'waiting',
      at: Date.now(),
    });
  }, []);

  const renderLobby = () => (
    <Lobby
      user={lobbyUser}
      socket={socket}
      hubSocketPhase={hubSocketPhase}
      onJoinTable={navigateToGameTable}
      onOpenHubJoinOnlineLobby={openHubJoinInOnlineLobby}
      onOpenWallet={openWalletForResourceRail}
      onOpenLocal={() => setCurrentView('local')}
      onOpenOnline={() => {
        setOnlineLobbyOrigin('lobby');
        setOnlineLobbyHubOpenCreateModal(false);
        setCurrentView('online-lobby');
      }}
      onOpenOnlineCreateRoom={() => openOnlineLobbyWithCreateModal('lobby')}
      onOpenBelot={() => setCurrentView('belot')}
      onOpenGifts={openFriendGiftsFromNav}
      onOpenFriends={openFriendsScreen}
      giftsNavBadgeCount={giftsNavBadgeCount}
      friendsNavBadgeCount={friendsNavBadgeCount}
    />
  );

  const renderView = () => {
    if (loading) {
      return (
        <div className="flex min-h-[65vh] items-center justify-center">
          <div className="w-12 h-12 border-2 border-neon-cyan border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    switch (currentView) {
      case 'auth':
        return <AuthPage onLogin={handleLogin} />;
      case 'lobby':
        return renderLobby();
      case 'poker-lobby':
        return (
          <PokerLobby
            user={lobbyUser}
            socket={socket}
            hubSocketPhase={hubSocketPhase}
            onOpenWallet={openWalletForResourceRail}
            onOpenPracticeTable={() => setCurrentView('poker-practice')}
            onOpenOnline={() => {
              setOnlineLobbyOrigin('poker-lobby');
              setOnlineLobbyHubOpenCreateModal(false);
              setCurrentView('online-lobby');
            }}
            onOpenOnlineCreateRoom={() => openOnlineLobbyWithCreateModal('poker-lobby')}
            onOpenBelot={() => setCurrentView('belot')}
            onBack={() => setCurrentView('lobby')}
            onJoinTable={navigateToGameTable}
            onOpenHubJoinOnlineLobby={openHubJoinInOnlineLobby}
            onOpenGifts={openFriendGiftsFromNav}
            onOpenFriends={openFriendsScreen}
            giftsNavBadgeCount={giftsNavBadgeCount}
            friendsNavBadgeCount={friendsNavBadgeCount}
          />
        );
      case 'belot':
        return (
          <BelotHub
            user={lobbyUser}
            socket={socket}
            hubSocketPhase={hubSocketPhase}
            onOpenWallet={openWalletForResourceRail}
            onOpenBelotGame={() => setCurrentView('belot-game')}
            onOpenOnline={() => {
              setOnlineLobbyOrigin('belot');
              setOnlineLobbyHubOpenCreateModal(false);
              setCurrentView('online-lobby');
            }}
            onOpenOnlineCreateRoom={() => openOnlineLobbyWithCreateModal('belot')}
            onBack={() => setCurrentView('lobby')}
            onJoinTable={navigateToGameTable}
            onOpenHubJoinOnlineLobby={openHubJoinInOnlineLobby}
            onOpenGifts={openFriendGiftsFromNav}
            onOpenFriends={openFriendsScreen}
            giftsNavBadgeCount={giftsNavBadgeCount}
            friendsNavBadgeCount={friendsNavBadgeCount}
          />
        );
      case 'belot-game':
        return (
          <BelotLobby
            user={lobbyUser}
            onUserPatch={handleUserProfilePatch}
            onBack={() => setCurrentView('belot')}
          />
        );
      case 'poker-practice':
        return (
          <PokerHuPractice
            user={lobbyUser}
            onBack={() => setCurrentView('poker-lobby')}
          />
        );
      case 'wallet':
        return (
          <CryptoWallet 
            user={user} 
            onBack={() => setCurrentView(walletReturnViewRef.current)}
          />
        );
      case 'profile':
        return (
          <LobbyProfileScreen
            user={lobbyUser}
            onBack={() => setCurrentView(profileReturnViewRef.current)}
            onUserPatch={handleUserProfilePatch}
            onNotifySuccess={setSuccess}
            onLogout={handleLogout}
          />
        );
      case 'local':
        return (
          <LocalGame
            user={lobbyUser}
            onUserPatch={handleUserProfilePatch}
            onBack={() => setCurrentView('lobby')}
          />
        );
      case 'mobile-local':
        return (
          <MobileLocalGame
            user={lobbyUser}
            onUserPatch={handleUserProfilePatch}
            onBack={() => setCurrentView('lobby')}
          />
        );
      case 'online-lobby':
        return (
          <OnlineLobby
            user={lobbyUser}
            accessToken={sessionToken}
            mode={onlineLobbyOrigin === 'poker-lobby' ? 'poker' : onlineLobbyOrigin === 'belot' ? 'belot' : 'seka'}
            onBack={() => {
              setOnlineLobbyHubPendingJoin(null);
              setOnlineLobbyHubOpenCreateModal(false);
              setCurrentView(onlineLobbyOrigin);
            }}
            hubPendingJoin={onlineLobbyHubPendingJoin}
            onHubPendingJoinConsumed={clearOnlineLobbyHubPendingJoin}
            hubOpenCreateModal={onlineLobbyHubOpenCreateModal}
            onHubOpenCreateModalConsumed={clearOnlineLobbyHubOpenCreateModal}
            onOpenGifts={openFriendGiftsFromNav}
            onOpenFriends={openFriendsScreen}
            giftsNavBadgeCount={giftsNavBadgeCount}
            friendsNavBadgeCount={friendsNavBadgeCount}
            acceptedFriends={acceptedFriendsForInvite}
            liveLobbyUsers={liveLobbyUsersForFriendInvite}
            friendServerPresenceByUserId={friendPresenceByUserId}
            onSendFriendRequest={sendFriendRequestToProfileUser}
            onLobbyFriendRequestInbound={handleLobbyFriendRequestDelivery}
            onLobbyFriendRequestResolved={handleLobbyFriendRequestResolved}
            onSocialInvite={handleSocialInviteEvent}
            onSocialTransportReconnected={onSocialLobbyTransportReconnected}
            onOpenMail={publicFlags.mailEnabled ? () => void openMailAndMarkRead() : undefined}
            mailUnreadCount={mailUnreadCount}
            reconnectWaitContext={onlineReconnectWaitContext}
            onStartGame={(gameState) => {
              const tableId = String(gameState?.roomCode || '').trim();
              if (!tableId) return;
              const mode =
                gameState?.mode ||
                (onlineLobbyOrigin === 'poker-lobby'
                  ? 'poker'
                  : onlineLobbyOrigin === 'belot'
                    ? 'belot'
                    : 'seka');
              navigateToGameTable({
                tableId,
                gameRoomId: String(gameState?.gameRoomId || '').trim(),
                mode: String(mode),
                returnView: 'online-lobby',
              });
            }}
          />
        );
      default:
        if (currentView.startsWith('game-')) {
          const tableSlug = currentView.slice('game-'.length).trim().toUpperCase();
          const joinTid = String(gameJoin?.tableId ?? '')
            .trim()
            .toUpperCase();
          if (!(gameJoin && joinTid && joinTid === tableSlug)) {
            return (
              <div className="relative z-10 mx-auto mt-10 w-full max-w-xl rounded-xl border border-amber-500/40 bg-card/85 p-5 text-center shadow-lg">
                <p className="text-sm font-semibold text-amber-100">
                  Сессия матча устарела. Вернитесь в онлайн-лобби и зайдите в стол заново.
                </p>
                <button
                  type="button"
                  onClick={() => setCurrentView('online-lobby')}
                  className="mt-4 rounded-lg border border-amber-400/45 bg-amber-500/15 px-4 py-2 text-sm font-semibold text-amber-100 transition hover:bg-amber-500/25"
                >
                  В онлайн-лобби
                </button>
              </div>
            );
          }
          const resolved = gameJoin;
          const mode = resolved.mode === 'poker' || resolved.mode === 'belot' ? resolved.mode : 'seka';
          const colyseusRoomName =
            resolved.mode === 'poker' || resolved.mode === 'belot' || resolved.mode === 'seka'
              ? resolved.mode
              : 'seka';
          if (mode === 'poker' || mode === 'belot') {
            return (
              <BaseGameRoomScreen
                tableId={resolved.tableId}
                gameRoomId={resolved.gameRoomId}
                colyseusRoomName={mode}
                user={lobbyUser}
                accessToken={sessionToken}
                onBack={handleGameBackFromRoom}
                incomingInvite={activeGameInvite}
                onRespondInvite={respondToTableInvite}
                onSessionOutcome={refreshAuthUser}
                returnView={resolved.returnView || 'online-lobby'}
                onMatchAborted={handleOnlineMatchAborted}
                onReconnectWait={handleOnlineReconnectWait}
              />
            );
          }
          return (
            <GameTable
              tableId={resolved.tableId}
              gameRoomId={resolved.gameRoomId}
              colyseusRoomName={colyseusRoomName}
              accessToken={sessionToken}
              user={lobbyUser}
              onBack={handleGameBackFromRoom}
              onSendFriendRequest={sendFriendRequestToProfileUser}
              incomingInvite={activeGameInvite}
              onRespondInvite={respondToTableInvite}
              onSessionOutcome={refreshAuthUser}
              returnView={resolved.returnView || 'online-lobby'}
              onMatchAborted={handleOnlineMatchAborted}
              onReconnectWait={handleOnlineReconnectWait}
            />
          );
        }
        if (SKIP_LOGIN_IN_DEV) {
          return renderLobby();
        }
        return <AuthPage onLogin={handleLogin} />;
    }
  };

  return (
    <div
      className={`pt-root App bg-background text-foreground antialiased relative ${
        showAppChrome ? 'flex min-h-[100dvh] flex-col overflow-hidden' : 'min-h-screen'
      }`}
    >
      <style>{`
        @keyframes app-toast-in {
          0% {
            opacity: 0;
            transform: translateY(-12px) scale(0.96);
          }
          60% {
            opacity: 1;
            transform: translateY(2px) scale(1.01);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes global-chat-preview-sheen {
          0% {
            opacity: 0;
            transform: translateX(-40%);
          }
          25% {
            opacity: 0.85;
          }
          100% {
            opacity: 0;
            transform: translateX(220%);
          }
        }
        @keyframes global-chat-preview-row-in {
          0% {
            opacity: 0;
            transform: translateY(5px) scale(0.985);
            filter: blur(1px);
          }
          70% {
            opacity: 1;
            filter: blur(0);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0);
          }
        }
        .global-chat-preview-sheen-beam {
          background: linear-gradient(
            100deg,
            transparent 0%,
            rgba(165, 243, 252, 0.2) 45%,
            rgba(34, 211, 238, 0.08) 55%,
            transparent 100%
          );
          animation: global-chat-preview-sheen 1.2s cubic-bezier(0.33, 1, 0.68, 1) forwards;
        }
        .global-chat-preview-row {
          animation: global-chat-preview-row-in 0.42s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        @media (prefers-reduced-motion: reduce) {
          .global-chat-preview-sheen-beam,
          .global-chat-preview-row {
            animation: none !important;
          }
        }
      `}</style>
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-neon-cyan/25 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-neon-orange/30 to-transparent" />
      </div>

      {showAppChrome && showHubResourceRail ? (
        <div
          aria-hidden
          className="pointer-events-none fixed inset-x-0 top-0 z-[6] mx-auto h-[100dvh] w-full max-w-[500px] bg-cover bg-top bg-no-repeat"
          style={{ backgroundImage: `url(${LOBBY_HOME_ASSETS.bgLobbyHeader})` }}
        />
      ) : null}

      {error ? <AppToast type="error" message={error} /> : null}
      {success ? <AppToast type="success" message={success} /> : null}

      {currentView === 'auth' && publicMaintenance.enabled ? (
        <div className="relative z-[125] mx-auto max-w-2xl px-3 pt-[max(0.5rem,env(safe-area-inset-top))] sm:px-4">
          <div
            className="rounded-xl border border-amber-400/40 bg-amber-950/55 px-3 py-2.5 text-center text-sm text-amber-50 shadow-lg backdrop-blur-sm"
            role="status"
          >
            <strong className="font-semibold">Технические работы.</strong>{' '}
            {publicMaintenance.allowedTesterUserIds?.length ? (
              <>
                После входа игра будет недоступна, кроме аккаунтов из белого списка тестировщиков (id заданы в
                админке).
              </>
            ) : (
              <>После входа игра будет недоступна, пока не завершим обслуживание.</>
            )}
            {publicMaintenance.retryAfterMinutes != null && publicMaintenance.retryAfterMinutes > 0 ? (
              <span>{' '}Ориентировочно зайдите через ~{publicMaintenance.retryAfterMinutes} мин.</span>
            ) : null}
          </div>
        </div>
      ) : null}

      {showAppChrome &&
        (showHubResourceRail ? (
          <div
            ref={appHeaderShellRef}
            className="app-floating-header fixed left-0 right-0 top-0 z-[35] border-b-0 bg-transparent shadow-none"
          >
            <div className="mx-auto max-w-[500px] overflow-x-hidden px-3 pb-2 pt-[env(safe-area-inset-top,0px)] sm:px-4 sm:pb-2.5">
            <LobbyHubHeader
              user={lobbyUser}
              connectionStatus={hubPhaseToHeaderConnectionStatus(hubSocketPhase)}
              onProfileClick={openProfileFromResourceRail}
              onOpenShop={openShopScreen}
              onOpenWallet={openWalletForResourceRail}
              onDailyRewards={publicFlags.dailyRewardsEnabled ? () => setDailyRewardsOpen(true) : undefined}
              onMail={publicFlags.mailEnabled ? () => void openMailAndMarkRead() : undefined}
              onClans={showHubTopActions ? () => setSuccess('Кланы — скоро') : undefined}
              showDailyBadge={Boolean(publicFlags.dailyRewardsEnabled && dailyRewardsHubClaimable)}
              mailUnreadCount={mailUnreadCount}
              globalChatSlot={
                showHubTopActions && publicFlags.globalChatEnabled ? (
                  <button
                    type="button"
                    onClick={() => setGlobalChatCollapsed((v) => !v)}
                    className="lobby-focus flex h-full min-h-0 w-full min-w-0 items-center gap-1.5 border-0 bg-transparent p-0 text-left outline-none transition active:scale-[0.99] sm:gap-2"
                    aria-expanded={!globalChatCollapsed}
                    aria-label={globalChatCollapsed ? 'Развернуть чат лобби' : 'Свернуть чат лобби'}
                  >
                    <span className="min-h-0 min-w-0 flex-1 overflow-hidden">
                      {globalLobbyMessages.length ? (
                        <div className="flex max-h-[3.25rem] min-h-0 flex-col gap-0.5 overflow-hidden sm:max-h-[3.5rem] sm:gap-1">
                          {globalChatHeaderPreview.map((m) => {
                            const lvl = getSenderDisplayLevel(m.sender, lobbyUser);
                            return (
                              <div
                                key={m.id}
                                className="flex min-w-0 items-center gap-1 text-[9px] leading-tight text-slate-200 sm:gap-1.5 sm:text-[10px]"
                              >
                                <GlobalChatMessageAvatar sender={m.sender} size="xs" />
                                <span className="min-w-0 flex-1 truncate">
                                  <span className="font-semibold text-cyan-100/95">{getSenderUsername(m.sender)}</span>
                                  <span className="text-slate-500"> · </span>
                                  <span className="font-bold tabular-nums text-amber-200/90">{lvl}</span>
                                  <span className="text-slate-500"> · </span>
                                  <span className="text-slate-400">{getSenderClan(m.sender)}</span>
                                  <span className="text-slate-500"> — </span>
                                  <span className="text-slate-100/90">{m.text}</span>
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="block truncate text-[9px] leading-snug text-slate-300 sm:text-[10px]">
                          Пока пусто, напиши первым
                        </span>
                      )}
                    </span>
                    <span className="flex shrink-0 flex-col items-center gap-0.5 self-center text-slate-300">
                      {globalChatCollapsed ? (
                        <ChevronDown className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
                      ) : (
                        <ChevronUp className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
                      )}
                      {dmNavUnreadCount > 0 ? (
                        <span
                          className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-violet-600 px-1 py-px text-[8px] font-black leading-none text-white"
                          title="Непрочитанные ЛС друзей"
                          aria-label={`Непрочитанные ЛС: ${dmNavUnreadCount > 99 ? '99+' : dmNavUnreadCount}`}
                        >
                          {dmNavUnreadCount > 99 ? '99+' : dmNavUnreadCount}
                        </span>
                      ) : null}
                    </span>
                  </button>
                ) : null
              }
            />
            {showHubTopActions && publicFlags.globalChatEnabled && !globalChatCollapsed ? (
              <section className="mt-2 flex min-h-0 flex-col rounded-2xl border border-cyan-300/20 bg-gradient-to-b from-[#0f1e31]/90 via-[#0b1624]/90 to-[#09111b]/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_12px_30px_rgba(0,0,0,0.35)] backdrop-blur-md">
                <div className="min-h-0 px-3 pb-3 pt-2 sm:px-4">
                  <div className="flex min-h-0 max-h-[min(78dvh,720px)] flex-col gap-2 sm:flex-row sm:gap-3">
                    <nav
                      className="flex shrink-0 flex-row gap-2 overflow-x-auto rounded-2xl border border-cyan-500/20 bg-gradient-to-b from-[#0c1829]/95 via-black/45 to-[#050a10]/95 p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_10px_28px_rgba(0,0,0,0.35)] sm:w-[11rem] sm:flex-col sm:gap-2.5 sm:overflow-visible sm:p-2.5"
                      role="tablist"
                      aria-label="Каналы чата"
                    >
                      {hubChatChannelDefs.map(({ id, label, labelLg, Icon }) => {
                        const active = hubChatChannel === id;
                        const activeClass =
                          id === 'global'
                            ? 'border-cyan-200/50 bg-gradient-to-br from-cyan-600/35 via-cyan-950/70 to-[#041018] text-cyan-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.14),0_0_0_1px_rgba(103,232,249,0.12),0_10px_28px_rgba(6,182,212,0.28)]'
                            : id === 'dm'
                              ? 'border-violet-300/45 bg-gradient-to-br from-violet-600/30 via-violet-950/75 to-[#0c0618] text-violet-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_0_0_1px_rgba(167,139,250,0.14),0_10px_28px_rgba(139,92,246,0.28)]'
                              : 'border-emerald-300/45 bg-gradient-to-br from-emerald-600/28 via-emerald-950/72 to-[#050f0c] text-emerald-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_0_0_1px_rgba(52,211,153,0.12),0_10px_28px_rgba(16,185,129,0.26)]';
                        const idleClass =
                          'border-white/[0.07] bg-black/30 text-slate-500 hover:border-cyan-400/20 hover:bg-white/[0.06] hover:text-slate-100 active:scale-[0.98]';
                        const navUnread = id === 'dm' ? dmNavUnreadCount : 0;
                        return (
                          <button
                            key={id}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => setHubChatChannel(id)}
                            className={`lobby-focus flex min-h-[3.1rem] min-w-[5.5rem] shrink-0 items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-left font-black uppercase tracking-[0.08em] transition duration-200 sm:min-h-[3.35rem] sm:w-full sm:justify-start sm:gap-2.5 sm:px-3.5 sm:py-3 ${
                              active ? activeClass : idleClass
                            } ${active ? 'ring-1 ring-white/10' : ''}`}
                          >
                            <span
                              className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border sm:h-10 sm:w-10 ${
                                active
                                  ? id === 'global'
                                    ? 'border-cyan-200/35 bg-cyan-500/15 text-cyan-100'
                                    : id === 'dm'
                                      ? 'border-violet-200/35 bg-violet-500/15 text-violet-100'
                                      : 'border-emerald-200/35 bg-emerald-500/15 text-emerald-100'
                                  : 'border-white/10 bg-black/40 text-slate-400'
                              }`}
                            >
                              <Icon className="h-[1.15rem] w-[1.15rem] sm:h-5 sm:w-5" strokeWidth={2.2} aria-hidden />
                            </span>
                            <span className="min-w-0 text-[11px] leading-tight sm:hidden">{label}</span>
                            <span className="hidden min-w-0 flex-1 truncate text-xs leading-tight sm:block sm:text-[13px]">
                              {labelLg}
                            </span>
                            {navUnread > 0 ? (
                              <span className="inline-flex min-w-[1.35rem] items-center justify-center rounded-full border border-violet-300/45 bg-violet-700/50 px-1.5 py-0.5 text-[10px] font-black leading-none text-violet-50">
                                {navUnread > 99 ? '99+' : navUnread}
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </nav>
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                      {hubChatChannel === 'global' ? (
                        <p className="mb-1.5 shrink-0 rounded-md border border-cyan-500/20 bg-cyan-950/25 px-2 py-1 text-[10px] leading-snug text-cyan-100/90 sm:text-xs">
                          Предупреждения и блокировка глобального чата сейчас только в этом браузере; общий бан на сервере —
                          позже.
                        </p>
                      ) : null}
                      {hubChatChannel === 'clan' ? (
                        <p className="mb-1.5 shrink-0 rounded-md border border-emerald-500/20 bg-emerald-950/25 px-2 py-1 text-[10px] leading-snug text-emerald-100/90 sm:text-xs">
                          Клановый чат пока локальный в браузере; синхронизация с сервером — позже.
                        </p>
                      ) : null}
                      {hubChatChannel === 'dm' ? (
                        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                          <p className="mb-1.5 shrink-0 rounded-md border border-violet-500/20 bg-violet-950/25 px-2 py-1 text-[10px] leading-snug text-violet-100/90 sm:text-xs">
                            Малявы синхронизируются через сервер для братвы, включая доставку после оффлайна.
                          </p>
                          <div className="flex min-h-0 min-w-0 flex-1 gap-1.5 overflow-hidden sm:gap-2">
                          <aside className="hub-dm-peer-scroll flex w-[3.35rem] shrink-0 flex-col items-stretch gap-1.5 overflow-y-auto rounded-lg border border-violet-400/25 bg-gradient-to-b from-violet-950/35 via-black/40 to-black/55 py-1.5 pl-1 pr-0.5 shadow-inner sm:w-[7.5rem] sm:gap-2 sm:px-1 sm:py-2">
                            {hubDmFriendsList.length === 0 ? (
                              <div className="flex flex-col items-center gap-1.5 px-0.5 text-center">
                                <p className="text-[8px] font-bold uppercase leading-tight text-violet-200/85 sm:text-[10px]">
                                  Нет друзей
                                </p>
                                <button
                                  type="button"
                                  onClick={openFriendsScreen}
                                  className="lobby-focus rounded-md border border-violet-400/35 bg-violet-950/50 px-1 py-0.5 text-[8px] font-semibold text-violet-100 sm:text-[10px]"
                                >
                                  Братва
                                </button>
                              </div>
                            ) : (
                              hubDmFriendsList.map((f) => {
                                const active = String(dmSelectedPeerId) === String(f.id);
                                const unread = Math.max(0, Number(dmUnreadByPeer[String(f.id)]) || 0);
                                return (
                                  <button
                                    key={f.id}
                                    type="button"
                                    onClick={() => setDmSelectedPeerId(String(f.id))}
                                    title={f.username}
                                    className={`lobby-focus flex flex-col items-center gap-0.5 rounded-lg border px-0.5 py-1 transition sm:flex-row sm:items-center sm:gap-2 sm:px-1.5 sm:py-1.5 ${
                                      active
                                        ? 'border-violet-300/50 bg-violet-600/25 shadow-[0_0_12px_rgba(139,92,246,0.25)]'
                                        : 'border-transparent bg-transparent hover:border-violet-400/25 hover:bg-white/[0.05]'
                                    }`}
                                  >
                                    <GlobalChatMessageAvatar sender={f} size="xs" />
                                    <span className="max-w-full truncate text-center text-[8px] font-semibold leading-tight text-violet-100/95 sm:text-left sm:text-[11px] sm:text-violet-100">
                                      {f.username}
                                    </span>
                                    {unread > 0 ? (
                                      <span className="rounded-full border border-violet-300/40 bg-violet-700/40 px-1.5 text-[8px] font-bold text-violet-100 sm:text-[9px]">
                                        {unread}
                                      </span>
                                    ) : null}
                                  </button>
                                );
                              })
                            )}
                          </aside>
                          <div
                            ref={globalChatScrollRef}
                            className="lobby-mp-scroll min-h-0 min-w-0 flex-1 space-y-2 overflow-y-auto rounded-xl border border-white/10 bg-gradient-to-b from-[#070d14]/90 via-black/35 to-black/20 p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] sm:p-2.5"
                          >
                            {!hubDmFriendsList.length ? (
                              <p className="rounded-lg border border-dashed border-violet-400/25 bg-violet-950/15 px-3 py-6 text-center text-xs leading-relaxed text-violet-100/90">
                                Добавьте братву в разделе «Братва», чтобы писать в личку.
                              </p>
                            ) : !hubActiveMessages.length ? (
                              <p className="rounded-lg border border-white/10 bg-black/25 px-3 py-6 text-center text-xs text-slate-400">
                                Пока нет сообщений с этим корешем — напишите первым.
                              </p>
                            ) : (
                              hubActiveMessages.map((m) => {
                                const isSystem = m.sender?.id === 'system' || m.sender?.clan === 'SYSTEM';
                                const openProfile = Boolean(m.sender?.id && m.sender.id !== 'system');
                                return (
                                  <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => {
                                      if (openProfile) setChatProfileUser(m.sender);
                                    }}
                                    className={`lobby-focus flex w-full min-w-0 gap-2.5 rounded-xl border px-2 py-2 text-left transition sm:gap-3 sm:px-2.5 sm:py-2.5 ${
                                      isSystem
                                        ? 'border-cyan-500/25 bg-cyan-950/20 hover:border-cyan-400/40'
                                        : 'border-white/8 bg-white/[0.04] hover:border-violet-300/35 hover:bg-white/[0.06]'
                                    } ${openProfile ? '' : 'cursor-default'}`}
                                  >
                                    <GlobalChatMessageAvatar sender={m.sender} />
                                    <div className="min-w-0 flex-1">
                                      <div className="flex min-w-0 flex-wrap items-center gap-1">
                                        <p className="min-w-0 max-w-[58%] truncate text-[11px] font-bold text-cyan-100 sm:text-xs">
                                          {getSenderUsername(m.sender)}
                                        </p>
                                        <ChatRankBadge level={getSenderDisplayLevel(m.sender, lobbyUser)} />
                                        <p className="min-w-0 flex-1 truncate text-[10px] font-medium text-cyan-200/75 sm:text-[11px]">
                                          <span className="text-slate-500">·</span> {getSenderClan(m.sender)}
                                        </p>
                                        <time
                                          className="ml-auto shrink-0 text-[10px] tabular-nums text-slate-500"
                                          dateTime={new Date(m.createdAt).toISOString()}
                                        >
                                          {new Date(m.createdAt).toLocaleTimeString('ru-RU', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          })}
                                        </time>
                                      </div>
                                      <p className="mt-1 break-words text-left text-xs leading-snug text-slate-100 sm:text-[13px]">
                                        {m.text}
                                      </p>
                                    </div>
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                        </div>
                      ) : (
                        <div
                          ref={globalChatScrollRef}
                          className="lobby-mp-scroll min-h-0 flex-1 space-y-2 overflow-y-auto rounded-xl border border-white/10 bg-gradient-to-b from-[#070d14]/90 via-black/35 to-black/20 p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] sm:p-2.5"
                        >
                          {hubActiveMessages.map((m) => {
                            const isSystem = m.sender?.id === 'system' || m.sender?.clan === 'SYSTEM';
                            const openProfile = Boolean(m.sender?.id && m.sender.id !== 'system');
                            return (
                              <button
                                key={m.id}
                                type="button"
                                onClick={() => {
                                  if (openProfile) setChatProfileUser(m.sender);
                                }}
                                className={`lobby-focus flex w-full min-w-0 gap-2.5 rounded-xl border px-2 py-2 text-left transition sm:gap-3 sm:px-2.5 sm:py-2.5 ${
                                  isSystem
                                    ? 'border-cyan-500/25 bg-cyan-950/20 hover:border-cyan-400/40'
                                    : 'border-white/8 bg-white/[0.04] hover:border-cyan-300/35 hover:bg-white/[0.06]'
                                } ${openProfile ? '' : 'cursor-default'}`}
                              >
                                <GlobalChatMessageAvatar sender={m.sender} />
                                <div className="min-w-0 flex-1">
                                  <div className="flex min-w-0 flex-wrap items-center gap-1">
                                    <p className="min-w-0 max-w-[58%] truncate text-[11px] font-bold text-cyan-100 sm:text-xs">
                                      {getSenderUsername(m.sender)}
                                    </p>
                                    <ChatRankBadge level={getSenderDisplayLevel(m.sender, lobbyUser)} />
                                    <p className="min-w-0 flex-1 truncate text-[10px] font-medium text-cyan-200/75 sm:text-[11px]">
                                      <span className="text-slate-500">·</span> {getSenderClan(m.sender)}
                                    </p>
                                    <time
                                      className="ml-auto shrink-0 text-[10px] tabular-nums text-slate-500"
                                      dateTime={new Date(m.createdAt).toISOString()}
                                    >
                                      {new Date(m.createdAt).toLocaleTimeString('ru-RU', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}
                                    </time>
                                  </div>
                                  <p className="mt-1 break-words text-left text-xs leading-snug text-slate-100 sm:text-[13px]">
                                    {m.text}
                                  </p>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                      <div className="mt-2 flex shrink-0 gap-2">
                        {hubChatChannel === 'global' ? (
                          <>
                            <input
                              type="text"
                              value={globalChatDraft}
                              onChange={(e) => setGlobalChatDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') sendGlobalLobbyChatMessage();
                              }}
                              placeholder={
                                isGlobalChatBlocked
                                  ? `Чат заблокирован на ${globalChatBlockedLeftMin} мин`
                                  : isGlobalChatCooldown
                                    ? `Следующее сообщение через ${globalChatCooldownLeftSec} сек`
                                    : 'Написать в глобальный чат...'
                              }
                              disabled={isGlobalChatSendLocked}
                              className="lobby-focus min-w-0 flex-1 rounded-xl border border-white/15 bg-black/30 px-3 py-2 text-sm text-slate-100 outline-none placeholder:text-slate-500 disabled:opacity-55"
                            />
                            <button
                              type="button"
                              onClick={sendGlobalLobbyChatMessage}
                              disabled={isGlobalChatSendLocked}
                              className="lobby-focus inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-cyan-400/35 bg-cyan-900/35 px-3 text-sm font-semibold text-cyan-100 transition hover:brightness-110 disabled:opacity-55"
                            >
                              <Send className="h-4 w-4" aria-hidden />
                              {isGlobalChatBlocked
                                ? `Бан ${globalChatBlockedLeftMin}м`
                                : isGlobalChatCooldown
                                  ? `Отпр. ${globalChatCooldownLeftSec}с`
                                  : 'Отправить'}
                            </button>
                          </>
                        ) : hubChatChannel === 'dm' ? (
                          <>
                            <input
                              type="text"
                              value={dmChatDraft}
                              onChange={(e) => setDmChatDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') sendAuxHubChatMessage();
                              }}
                              placeholder={
                                !hubDmFriendsList.length
                                  ? 'Сначала добавьте друзей…'
                                  : !dmSelectedPeerId
                                    ? 'Выберите кореша слева'
                                    : `Малява для ${hubDmFriendsList.find((f) => String(f.id) === String(dmSelectedPeerId))?.username || 'кореша'}…`
                              }
                              disabled={!hubDmFriendsList.length || !dmSelectedPeerId}
                              className="lobby-focus min-w-0 flex-1 rounded-xl border border-violet-300/25 bg-black/30 px-3 py-2 text-sm text-slate-100 outline-none placeholder:text-slate-500 disabled:opacity-55"
                            />
                            <button
                              type="button"
                              onClick={sendAuxHubChatMessage}
                              disabled={!hubDmFriendsList.length || !dmSelectedPeerId}
                              className="lobby-focus inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-violet-400/35 bg-violet-950/40 px-3 text-sm font-semibold text-violet-100 transition hover:brightness-110 disabled:opacity-55"
                            >
                              <Send className="h-4 w-4" aria-hidden />
                              {'Отправить'}
                            </button>
                          </>
                        ) : (
                          <>
                            <input
                              type="text"
                              value={clanChatDraft}
                              onChange={(e) => setClanChatDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') sendAuxHubChatMessage();
                              }}
                              placeholder={
                                !hasHubClanChat
                                  ? 'Нужен клан, чтобы писать в клановый чат'
                                  : isClanSendLocked
                                    ? `Клан: подождите ${clanChatCooldownLeftSec} сек`
                                    : `Сообщение клану «${String(lobbyUser?.clan || '').trim()}» (локально)...`
                              }
                              disabled={isClanSendLocked}
                              className="lobby-focus min-w-0 flex-1 rounded-xl border border-emerald-400/25 bg-black/30 px-3 py-2 text-sm text-slate-100 outline-none placeholder:text-slate-500 disabled:opacity-55"
                            />
                            <button
                              type="button"
                              onClick={sendAuxHubChatMessage}
                              disabled={isClanSendLocked}
                              className="lobby-focus inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-emerald-400/40 bg-emerald-950/35 px-3 text-sm font-semibold text-emerald-100 transition hover:brightness-110 disabled:opacity-55"
                            >
                              <Send className="h-4 w-4" aria-hidden />
                              {!hasHubClanChat ? '—' : isClanSendLocked ? `${clanChatCooldownLeftSec}с` : 'Отправить'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            ) : null}
            </div>
          </div>
        ) : (
          <div
            ref={appHeaderShellRef}
            className="app-floating-header app-header-chrome fixed left-0 right-0 top-0 z-[35] border-b border-border/50 border-white/[0.06] bg-[#02050c]/92 shadow-[0_8px_28px_rgba(0,0,0,0.45)] backdrop-blur-md supports-[backdrop-filter]:bg-[#02050c]/85"
          >
            <div className="relative mx-auto flex max-w-6xl items-center justify-center gap-2 px-3 pb-3 pt-[max(1.25rem,env(safe-area-inset-top,0px))] sm:px-4">
            {appChromePokerContext ? (
              <Club className="h-7 w-7 text-emerald-400 sm:h-8 sm:w-8" aria-hidden />
            ) : appChromeBelotContext ? (
              <Sparkles className="h-7 w-7 text-violet-400 sm:h-8 sm:w-8" aria-hidden />
            ) : (
              <Spade className="h-7 w-7 text-[#c9a227] sm:h-8 sm:w-8" aria-hidden />
            )}
            <div className="text-center">
              <h1 className="text-2xl font-bold tracking-wide text-foreground sm:text-3xl">{appChromeTitle}</h1>
              <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">
                {appChromeSubtitle}
              </p>
            </div>
            {user?.username && (
              <div className="absolute right-3 top-2 rounded-full border border-neon-cyan/35 bg-card/85 px-2.5 py-1 text-[11px] font-semibold text-neon-cyan sm:right-4">
                {user.username}
              </div>
            )}
            </div>
          </div>
        ))}

      <div
        className={
          showAppChrome
            ? `relative z-10 mx-auto flex min-h-0 w-full flex-1 flex-col overflow-hidden px-3 pb-16 sm:px-4 ${showHubResourceRail ? 'max-w-[500px]' : 'max-w-6xl'}`
            : 'relative z-10 min-h-0 w-full'
        }
        style={
          showAppChrome && appHeaderMeasurePx != null
            ? { '--app-header-h': `${appHeaderMeasurePx}px`, paddingTop: appHeaderMeasurePx }
            : undefined
        }
      >
        {showAppChrome && appHeaderMeasurePx == null ? (
          <div className="h-[var(--app-header-h)] shrink-0" aria-hidden="true" />
        ) : null}
        {showAppChrome ? (
          <div
            ref={appMainScrollRef}
            className="app-main-scroll flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain"
            style={{
              scrollPaddingTop:
                'calc(var(--app-header-h, 12rem) + max(0.5rem, env(safe-area-inset-top, 0px)))',
            }}
          >
            {renderView()}
          </div>
        ) : (
          renderView()
        )}
      </div>

      {globalInviteOverlayInvite && !shouldShowMaintenanceLock ? (
        <div
          className={`fixed right-3 z-[125] w-[min(92vw,380px)] ${
            showAppChrome
              ? 'top-[max(0.75rem,calc(env(safe-area-inset-top,0px)+var(--app-header-h,12rem)+0.35rem))]'
              : 'top-[max(0.75rem,env(safe-area-inset-top,0px))]'
          }`}
        >
          <section className="rounded-xl border border-amber-400/45 bg-gradient-to-b from-[#22180f]/95 via-[#18120d]/95 to-[#100d09]/95 p-3 shadow-[0_14px_34px_rgba(0,0,0,0.48)] backdrop-blur-sm">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[10px] font-black uppercase tracking-[0.14em] text-amber-200/95">
                  Входящий вызов
                </p>
                <p className="mt-1 truncate text-sm font-bold text-amber-50">
                  {globalInviteOverlayInvite.fromUsername || 'Игрок'}
                </p>
                <p className="mt-0.5 text-[11px] text-slate-300">
                  {String(globalInviteOverlayInvite.mode || 'seka').toUpperCase()} · код{' '}
                  <span className="font-mono text-amber-200">{globalInviteOverlayInvite.roomCode || '—'}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => respondToTableInvite(globalInviteOverlayInvite, 'declined')}
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-white/15 bg-black/35 text-slate-200"
                aria-label="Скрыть приглашение"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <div className="mt-2.5 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => respondToTableInvite(globalInviteOverlayInvite, 'accept-now')}
                className="rounded-lg border border-emerald-400/40 bg-emerald-500/20 px-3 py-1.5 text-xs font-bold text-emerald-100"
              >
                Принять
              </button>
              <button
                type="button"
                onClick={() => respondToTableInvite(globalInviteOverlayInvite, 'declined')}
                className="rounded-lg border border-rose-400/40 bg-rose-500/20 px-3 py-1.5 text-xs font-bold text-rose-100"
              >
                Отклонить
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {shouldShowMaintenanceLock ? (
        <MaintenanceLockScreen
          message={publicMaintenance.message}
          retryAfterMinutes={publicMaintenance.retryAfterMinutes}
          username={lobbyUser?.username || null}
          onLogout={handleLogout}
        />
      ) : null}
      {loginCarouselOpen && loginCarouselSlides.length > 0 && !shouldShowMaintenanceLock ? (
        <LoginPromoCarousel slides={loginCarouselSlides} version={loginCarouselVersion} onDismiss={dismissLoginCarousel} />
      ) : null}
      {dailyRewardsOpen ? (
        <LobbyDailyRewardsScreen
          onBack={() => setDailyRewardsOpen(false)}
          onClaimSuccess={handleDailyRewardClaim}
        />
      ) : null}
      {friendsOpen ? (
        <LobbyFriendsScreen
          onBack={() => setFriendsOpen(false)}
          user={lobbyUser}
          onToast={setSuccess}
          onOpenFriendGifts={openFriendGiftsFromFriendsScreen}
          onAppendGlobalMessage={appendGlobalLobbyMessage}
          globalFriendRequests={mailFriendRequests}
          onGlobalFriendRequestAction={handleMailRequestAction}
          onSendFriendRequest={sendFriendRequestToProfileUser}
          onRemoveAcceptedFriend={removeAcceptedFriendByPeerId}
          onOpenFriendDm={openFriendDmFromFriendsScreen}
          accessToken={sessionToken}
          useServerFriendRequests={Boolean(sessionToken)}
          liveLobbyUsers={liveLobbyUsersForFriendInvite}
          friendServerPresenceByUserId={friendPresenceByUserId}
          onChallengeFriend={sendFriendGameChallenge}
          incomingTableInvites={incomingTableInvites}
          outgoingTableInvites={outgoingTableInvites}
          onRespondTableInvite={respondToTableInvite}
        />
      ) : null}
      {friendGiftsOpen ? (
        <LobbyFriendGiftsScreen
          onBack={() => setFriendGiftsOpen(false)}
          sessionToken={sessionToken}
          onToast={setSuccess}
          onIncomingCountChange={setGiftsNavBadgeCount}
          onBalanceUpdate={handleFriendGiftsBalanceUpdate}
        />
      ) : null}
      {publicFlags.shopEnabled && shopOpen ? (
        <LobbyShopScreen
          onBack={() => setShopOpen(false)}
          onPurchase={handleShopPurchase}
          userBalance={lobbyUser?.balance}
          userCash={lobbyUser?.bucksBalance ?? lobbyUser?.realBalance ?? lobbyUser?.cashBalance}
          catalog={shopCatalog}
          purchasesEnabled={publicFlags.shopPurchasesEnabled}
          stripeCheckoutConfigured={publicFlags.stripeCheckoutConfigured}
          checkoutBlockedNoSession={Boolean(
            publicFlags.shopPurchasesEnabled && publicFlags.stripeCheckoutConfigured && !sessionToken
          )}
          onViewOnlyPurchaseAttempt={() =>
            setError('Покупки отключены: каталог только для ознакомления (флаг shopPurchasesEnabled на сервере).')
          }
        />
      ) : null}
      {publicFlags.mailEnabled && mailOpen ? (
        <div
          className="fixed inset-0 z-[115] overflow-y-auto overscroll-contain bg-black/70 px-3 py-[max(0.75rem,env(safe-area-inset-top,0px))] pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] backdrop-blur-sm"
          role="presentation"
          onClick={() => setMailOpen(false)}
        >
          <div className="flex min-h-full w-full items-start justify-center py-3 sm:min-h-[100dvh] sm:items-center sm:py-2">
            <section
              role="dialog"
              aria-modal="true"
              aria-labelledby="mail-modal-title"
              className="flex h-[min(88dvh,calc(100dvh-2rem))] max-h-[min(88dvh,calc(100dvh-2rem))] min-h-0 w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-violet-300/25 bg-gradient-to-b from-[#1a1833] via-[#141227] to-[#0d0b1b] shadow-[0_20px_50px_rgba(0,0,0,0.55)]"
              onClick={(e) => e.stopPropagation()}
            >
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-white/10 px-4 pb-3 pt-4 sm:px-5">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-violet-300/30 bg-violet-950/50 text-violet-100">
                  <Mail className="h-5 w-5" strokeWidth={2.1} aria-hidden />
                </span>
                <div className="min-w-0">
                  <p id="mail-modal-title" className="text-sm font-black uppercase tracking-wide text-violet-100">
                    ПРОГОНЫ С ЦЕНТРАЛА
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMailOpen(false)}
                className="lobby-focus flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-black/30 text-slate-200"
                aria-label="Закрыть прогон"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
            <div className="min-h-0 flex-1 touch-pan-y space-y-2 overflow-y-scroll overscroll-y-contain px-4 py-4 sm:px-5 [-webkit-overflow-scrolling:touch] [scrollbar-color:rgba(167,139,250,0.45)_transparent] [scrollbar-width:thin]">
              {sortedLobbyInbox.length ? (
                sortedLobbyInbox.map((item) => (
                    <article
                      key={item.id}
                      className={`min-w-0 rounded-xl border px-3 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] ${
                        item.read
                          ? 'border-white/10 bg-white/[0.03]'
                          : 'border-violet-400/35 bg-violet-950/25'
                      }`}
                    >
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <span
                          className={`rounded-md border border-white/10 bg-black/30 px-2 py-0.5 font-bold uppercase tracking-wide text-violet-200 ${
                            item.kind === 'broadcast' ? 'max-w-[100%] text-[9px] leading-tight sm:text-[10px]' : 'text-[10px]'
                          }`}
                        >
                          {inboxKindLabel(item.kind)}
                        </span>
                        {!item.read ? (
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-300">Новое</span>
                        ) : null}
                      </div>
                      <h2 className="break-words text-sm font-bold text-white">{item.title}</h2>
                      {item.kind === 'broadcast' ? null : item.fromUsername ? (
                        <p className="mt-0.5 text-xs text-cyan-200/90">
                          ПРОГОН ОТ {String(item.fromUsername || '').toUpperCase()}
                        </p>
                      ) : isVorInboxMessage(item) ? (
                        <p className="mt-0.5 text-xs text-cyan-200/90">
                          ПРОГОН ОТ{' '}
                          <span className="animate-pulse text-sm font-black uppercase tracking-wide underline decoration-double underline-offset-2 sm:text-base">
                            ВОРА
                          </span>
                        </p>
                      ) : null}
                      <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                        <button
                          type="button"
                          className="lobby-focus inline-flex items-center gap-1 rounded-lg border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs font-semibold text-slate-200 transition hover:bg-white/10"
                          onClick={() =>
                            setMailExpandedById((prev) => ({
                              ...prev,
                              [item.id]: !(prev[item.id] === true),
                            }))
                          }
                          aria-expanded={mailExpandedById[item.id] === true}
                          aria-controls={`mail-body-${item.id}`}
                          id={`mail-toggle-${item.id}`}
                        >
                          {mailExpandedById[item.id] === true ? (
                            <>
                              <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
                              Свернуть
                            </>
                          ) : (
                            <>
                              <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                              Развернуть
                            </>
                          )}
                        </button>
                      </div>
                      {mailExpandedById[item.id] !== true && item.body ? (
                        <p className="mt-2 line-clamp-2 min-w-0 break-words text-xs leading-snug text-slate-500 [overflow-wrap:anywhere]">
                          {item.body}
                        </p>
                      ) : null}
                      {mailExpandedById[item.id] !== true &&
                      !String(item.body || '').trim() &&
                      (item.rewardCoins > 0 || item.imageUrl || item.linkUrl) ? (
                        <p className="mt-2 text-xs text-slate-500">
                          Нажми «Развернуть» — тут есть ещё контент.
                        </p>
                      ) : null}
                      <div
                        id={`mail-body-${item.id}`}
                        role="region"
                        aria-labelledby={`mail-toggle-${item.id}`}
                        hidden={mailExpandedById[item.id] !== true}
                      >
                        <p className="mt-2 min-w-0 break-words text-sm leading-relaxed text-slate-300 whitespace-pre-wrap [overflow-wrap:anywhere]">
                          {item.body}
                        </p>
                        {item.imageUrl ? (
                          <div className="mt-3 overflow-hidden rounded-lg border border-white/10 bg-black/20">
                            <img
                              src={item.imageUrl}
                              alt=""
                              className="max-h-48 w-full object-contain"
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                        ) : null}
                        {item.linkUrl ? (
                          <p className="mt-3">
                            <a
                              href={item.linkUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-semibold text-violet-300 underline decoration-violet-400/50 underline-offset-2 hover:text-violet-200"
                            >
                              {item.linkLabel || 'Открыть ссылку'}
                            </a>
                          </p>
                        ) : null}
                        {item.rewardCoins > 0 ? (
                          <div className="mt-3 rounded-xl border border-amber-400/40 bg-gradient-to-b from-amber-950/35 to-black/25 px-3 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                            <div className="mb-2 flex flex-wrap items-center gap-2">
                              <span className="rounded-md border border-amber-300/55 bg-amber-500/25 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-amber-100 shadow-[0_0_12px_rgba(251,191,36,0.2)]">
                                Ништяк
                              </span>
                              <span className="text-xs font-semibold tabular-nums text-amber-100/95">
                                {item.rewardCoins.toLocaleString('ru-RU')} монет
                              </span>
                            </div>
                            {sessionToken ? (
                              item.rewardClaimed ? (
                                <p className="text-xs font-semibold text-emerald-300">
                                  Ништяк уже забран — с чистой совестью чифирнул.
                                </p>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleClaimInboxReward(item.id)}
                                  className="lobby-focus flex w-full flex-col items-center justify-center gap-0.5 rounded-xl border border-amber-400/50 bg-gradient-to-b from-amber-600/95 to-amber-900/95 px-4 py-3 text-white shadow-[0_8px_24px_rgba(245,158,11,0.28)] transition hover:brightness-110"
                                >
                                  <span className="text-sm font-black uppercase tracking-wide">
                                    Забрать ништяк
                                  </span>
                                  <span className="text-center text-[11px] font-semibold leading-snug text-amber-50/95">
                                    Чифирнуть · {item.rewardCoins.toLocaleString('ru-RU')} монет
                                  </span>
                                </button>
                              )
                            ) : (
                              <p className="text-xs text-slate-500">
                                Войди в аккаунт — иначе ништяк не заберёшь.
                              </p>
                            )}
                          </div>
                        ) : null}
                        <p className="mt-2 text-[10px] text-slate-500">
                          {new Date(item.createdAt).toLocaleString('ru-RU', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </article>
                  ))
              ) : (
                <p className="rounded-xl border border-dashed border-white/15 bg-black/20 px-4 py-8 text-center text-sm text-slate-400">
                  С централа пока тишина — прогонов нет.
                </p>
              )}
            </div>
            </section>
          </div>
        </div>
      ) : null}
      {chatProfileUser ? (
        <div className="fixed inset-0 z-[120] overflow-y-auto overscroll-contain bg-black/65 backdrop-blur-sm">
          <div className="flex min-h-[100dvh] w-full items-center justify-center px-3 py-6">
            <section className="w-full max-w-sm max-h-[min(85dvh,calc(100dvh-1.5rem))] overflow-y-auto rounded-2xl border border-cyan-300/25 bg-gradient-to-b from-[#132238] to-[#0b1320] p-3 shadow-[0_16px_44px_rgba(0,0,0,0.55)]">
            <div className="mb-3 flex items-start justify-between gap-2">
              <p className="text-sm font-black uppercase tracking-wide text-cyan-200">Профиль игрока</p>
              <button
                type="button"
                onClick={() => setChatProfileUser(null)}
                className="lobby-focus flex h-8 w-8 items-center justify-center rounded-lg border border-white/15 bg-black/25 text-slate-300"
                aria-label="Закрыть профиль игрока"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>

            <div className="mb-3 flex items-center gap-3 rounded-xl border border-white/10 bg-black/25 p-3">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-amber-500/50 bg-gradient-to-br from-[#1e3a5f] to-[#0f1c2e] text-xl font-black text-white">
                {chatProfileUser.avatarUrl ? (
                  <img src={chatProfileUser.avatarUrl} alt="" className="h-full w-full object-cover" draggable={false} />
                ) : (
                  (chatProfileUser.username || 'G').charAt(0).toUpperCase()
                )}
              </div>
              <div className="min-w-0">
                <p className="truncate text-base font-bold text-white">{chatProfileUser.username}</p>
                <p className="text-xs text-cyan-200/90">Клан: {chatProfileUser.clan || 'Без клана'}</p>
                <p className="text-xs text-slate-300">Уровень: {Number(chatProfileUser.level) || 1}</p>
              </div>
            </div>

            {chatProfileFriendship.kind === 'friends' ? (
              <div className="flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-400/35 bg-emerald-950/35 px-4 py-3 text-sm font-bold uppercase tracking-wide text-emerald-100">
                <BadgeCheck className="h-5 w-5 shrink-0 text-emerald-300" aria-hidden />
                Братва
              </div>
            ) : null}
            {chatProfileFriendship.kind === 'incoming_pending' ? (
              <div className="space-y-2">
                <p className="rounded-xl border border-amber-400/30 bg-amber-950/25 px-3 py-2 text-center text-xs leading-snug text-amber-100/95">
                  Игрок отправил запрос закорефаниться. Откройте раздел «Братва», чтобы принять или отклонить.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setChatProfileUser(null);
                    openFriendsScreen();
                  }}
                  className="lobby-focus inline-flex w-full items-center justify-center gap-2 rounded-xl border border-amber-400/45 bg-gradient-to-b from-amber-600/95 to-amber-900/95 px-4 py-3 text-sm font-bold uppercase tracking-wide text-white shadow-[0_8px_24px_rgba(245,158,11,0.22)] transition hover:brightness-110"
                >
                  Открыть «Братва»
                </button>
              </div>
            ) : null}
            {chatProfileFriendship.kind === 'outgoing_pending' ? (
              chatProfileFriendship.resendLeftMs > 0 ? (
                <div className="flex w-full flex-col items-center justify-center gap-1 rounded-xl border border-slate-500/35 bg-black/30 px-4 py-3 text-center">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-slate-300">Ожидание ответа</span>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold tabular-nums text-cyan-200/90">
                    <Clock className="h-4 w-4 shrink-0 text-cyan-300/80" aria-hidden />
                    Повторная заявка через {formatCountdownMmSs(chatProfileFriendship.resendLeftMs)}
                  </span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleSendFriendRequestFromChatProfile}
                  className="lobby-focus inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-400/40 bg-gradient-to-b from-emerald-500 to-emerald-800 px-4 py-3 text-sm font-bold uppercase tracking-wide text-white shadow-[0_8px_24px_rgba(16,185,129,0.28)] transition hover:brightness-110"
                >
                  <UserPlus className="h-4 w-4" aria-hidden />
                  Отправить запрос закорефаниться снова
                </button>
              )
            ) : null}
            {chatProfileFriendship.kind === 'none' ? (
              <button
                type="button"
                onClick={handleSendFriendRequestFromChatProfile}
                className="lobby-focus inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-400/40 bg-gradient-to-b from-emerald-500 to-emerald-800 px-4 py-3 text-sm font-bold uppercase tracking-wide text-white shadow-[0_8px_24px_rgba(16,185,129,0.28)] transition hover:brightness-110"
              >
                <UserPlus className="h-4 w-4" aria-hidden />
                Закорефаниться
              </button>
            ) : null}
            </section>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default App;
